/* eslint-disable no-unused-vars */
import React, { useState, useEffect } from 'react';
import { db, auth } from '../firebase';
import { getStorage, ref as storageRef, getDownloadURL } from 'firebase/storage';
import {
  collection,
  getDocs,
  query,
  where,
  deleteDoc,
  doc,
  orderBy,
  serverTimestamp,
  getDoc,
  updateDoc,
  addDoc
} from 'firebase/firestore';
import { onSnapshot } from '../utils/firestoreListen';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import BackButton from '../components/BackButton';
import ReimagineScrollerCard from '../components/ReimagineScrollerCard';
import ArtHistoryCard from '../components/ArtHistoryCard';
import { getTransformedArtworksForOriginal, dedupeWaysOfSeeingActivitiesByArtworkStyle } from '../utils/activityService';
import { canShareArtworkPublicly, isUnder13 } from '../utils/consentUtils';
import { FaTrash, FaEye, FaBook, FaGraduationCap, FaGlobe, FaEdit, FaCheckCircle, FaStar, FaExpandArrowsAlt, FaTimes, FaArrowLeft, FaChalkboardTeacher, FaUsers, FaChartBar, FaComments, FaShare, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import { motion, AnimatePresence } from 'framer-motion';
import ConsistentLoader from '../components/ConsistentLoader';
import KidBackground from '../ui/backgrounds/KidBackground';
import MentorAccessCheck from '../components/MentorAccessCheck';
import { warmResolvedImageUrls } from '../utils/urlResolver';
import SmartImage from '../components/SmartImage';

export default function MentorActivityDashboard() {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [selectedArtworkGroup, setSelectedArtworkGroup] = useState(null);
  const [resolvedOriginalImageUrl, setResolvedOriginalImageUrl] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null); // { artworkUrl, title }
  const [detailsOpen, setDetailsOpen] = useState({ appreciation: false, techniques: false, movements: false, concepts: false });
  const [selectedPointIdx, setSelectedPointIdx] = useState(null);
  const [classFilter, setClassFilter] = useState(() => {
    try { return localStorage.getItem('mentorClass') || ''; } catch { return ''; }
  });
  const [sectionFilter, setSectionFilter] = useState(() => {
    try { return localStorage.getItem('mentorSection') || ''; } catch { return ''; }
  });
  const [stats, setStats] = useState({
    totalActivities: 0,
    completedActivities: 0,
    features: {
      'art-appreciation': 0,
      'art-history': 0,
      'art-compare': 0,
      'reimagine-artwork': 0,
      'art-story': 0,
      'tutorial': 0
    }
  });
  const [orgStats, setOrgStats] = useState(null);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showShareOptionsModal, setShowShareOptionsModal] = useState(false);
  const [selectedArtworkForShare, setSelectedArtworkForShare] = useState(null);
  const [allArtworks, setAllArtworks] = useState([]);
  const [shareFormData, setShareFormData] = useState({
    studentName: '',
    className: '',
    section: '',
    schoolName: '',
    mentorComments: ''
  });
  const [shareOptions, setShareOptions] = useState({
    artmosphereMentors: false,
    organizationMentors: false,
    allowAccessCode: false
  });
  const [currentArtworkIndex, setCurrentArtworkIndex] = useState(0);
  const [waysOfSeeingActivities, setWaysOfSeeingActivities] = useState({}); // Map of artwork group key -> array of activities
  const [resolvedArtworkUrls, setResolvedArtworkUrls] = useState({}); // Map of artwork group key -> resolved original artwork URL
  useEffect(() => {
    const activityUrls = (activities || []).flatMap((activity) => [
      activity?.imageUrl,
      activity?.artworkUrl,
      activity?.originalImageUrl,
      activity?.transformedImageUrl,
      activity?.metadata?.originalImageUrl,
      activity?.metadata?.imageUrl,
      activity?.metadata?.artworkUrl
    ]);
    const sharedUrls = (allArtworks || []).flatMap((art) => [
      art?.imageUrl,
      art?.artworkUrl,
      art?.originalImageUrl,
      art?.transformedImageUrl
    ]);
    const groupedUrls = Object.values(resolvedArtworkUrls || {});
    warmResolvedImageUrls([...activityUrls, ...sharedUrls, ...groupedUrls].filter(Boolean), 48).catch(() => {});
  }, [activities, allArtworks, resolvedArtworkUrls]);
  const getArtworkGroupKey = (group) => {
    if (!group) return null;
    return group.artworkUrl ||
      group.activities?.[0]?.metadata?.imageHash ||
      group.activities?.[0]?.originalImageUrl ||
      group.activities?.[0]?.artworkUrl ||
      group.activities?.[0]?.metadata?.originalImageUrl ||
      group.title ||
      null;
  };

  const storage = getStorage();
  const isBlobUrl = (url) => typeof url === 'string' && url.startsWith('blob:');
  const getNonBlobUrl = (...urls) => {
    for (const url of urls) {
      if (url && !isBlobUrl(url)) {
        return url;
      }
    }
    return null;
  };
  const resolveUrl = async (url) => {
    if (!url) return null;
    if (typeof url === 'string' && url.startsWith('gs://')) {
      try {
        const fileRef = storageRef(storage, url);
        return await getDownloadURL(fileRef);
      } catch (err) {
        console.warn('Failed to resolve gs:// url', url, err?.message || err);
        return null;
      }
    }
    return url;
  };
  const resolveFirstValidUrl = async (...urls) => {
    for (const rawUrl of urls) {
      const cleaned = getNonBlobUrl(rawUrl);
      if (!cleaned) continue;
      const resolved = await resolveUrl(cleaned);
      if (resolved) return resolved;
    }
    return null;
  };

  const buildStyleInfo = (activity = {}) => {
    // First check activity.styleInfo directly (this is where OpenAI-generated data is stored)
    if (activity.styleInfo?.name) {
      return activity.styleInfo;
    }
    
    // Then check metadata
    const metadata = activity.metadata || {};
    if (metadata.styleInfo?.name) return metadata.styleInfo;
    if (metadata.selectedStyle?.name) return metadata.selectedStyle;
    if (metadata.style?.name) return metadata.style;
    if (metadata.styleName) {
      return {
        id: metadata.styleId || metadata.styleInfo?.id || metadata.selectedStyle?.id || metadata.style?.id,
        name: metadata.styleName,
        description: metadata.styleDescription || metadata.styleInfo?.description || metadata.selectedStyle?.description || metadata.style?.description
      };
    }
    return null;
  };
  
  const navigate = useNavigate();
  const { user, isAuthenticated, logout } = useAuth();

  // Get mentor ID from Firebase user or mentor_session
  const getMentorId = () => {
    if (user?.uid) return user.uid;
    try {
      const session = JSON.parse(localStorage.getItem('mentor_session') || '{}');
      // For mentor_session, we need to find the actual mentor document
      // The ownerId in session is the organization owner, not the mentor
      // We'll need to query by email to find the mentor's activities
      return session?.email || null;
    } catch (_) {
      return null;
    }
  };

  // Check if user has mentor session (access code login)
  const hasMentorSession = () => {
    try {
      const session = JSON.parse(localStorage.getItem('mentor_session') || '{}');
      return !!(session && session.ownerId && session.email);
    } catch (_) {
      return false;
    }
  };

  const handleSignOut = async () => {
    try {
      await logout();
    } catch (_) {}
    try {
      localStorage.removeItem('mentor_session');
      window.dispatchEvent(new Event('mentor_session_changed'));
    } catch (_) {}
    navigate('/');
  };

  // Fetch all artworks for sharing
  const fetchAllArtworks = async () => {
    const mentorId = getMentorId();
    if (!mentorId) {
      console.warn('No mentor ID available for fetching artworks');
      return;
    }

    try {
      console.log('📥 Fetching uploaded artworks tagged to student names for sharing...');
      
      // Query userActivities collection for mentor activities with uploaded artworks
      // These are artworks uploaded through "Lets Play with Art" with student name, class, section
      // For mentor_session, we need to query by email instead of UID
      let activitiesQuery;
      if (user?.uid) {
        activitiesQuery = query(
          collection(db, 'userActivities'),
          where('metadata.context', '==', 'mentor'),
          where('metadata.mentorId', '==', user.uid)
        );
      } else {
        // For mentor_session, query by mentorEmail
        activitiesQuery = query(
          collection(db, 'userActivities'),
          where('metadata.context', '==', 'mentor'),
          where('metadata.mentorEmail', '==', mentorId.toLowerCase())
        );
      }

      const snapshot = await getDocs(activitiesQuery);
      console.log('📊 Found', snapshot.size, 'mentor activities');
      
      const artworksArray = [];

      snapshot.docs.forEach(doc => {
        const data = doc.data();
        
        // Exclude art history activities (appreciation and comparison)
        const feature = data.metadata?.feature || data.feature || '';
        const activityType = data.type || '';
        
        if (feature === 'art-history-appreciation' || 
            feature === 'art-comparison' ||
            activityType === 'art-history-appreciation' ||
            activityType === 'art-comparison') {
          console.log('Skipping art history activity:', doc.id, 'feature:', feature);
          return;
        }
        
        // Only include activities from "Lets Play with Art" feature
        // This includes: ways-of-seeing, reimagine, reimagine-artwork, etc.
        const isLetsPlayWithArt = feature === 'ways-of-seeing' ||
                                  feature === 'ways-of-seeing-journey' ||
                                  feature === 'reimagine' ||
                                  feature === 'reimagine-artwork' ||
                                  feature === 'reimagine-with-feedback' ||
                                  activityType === 'reimagine';
        
        if (!isLetsPlayWithArt) {
          console.log('Skipping non-Lets Play with Art activity:', doc.id, 'type:', activityType, 'feature:', feature);
          return;
        }
        
        // Only include activities that have student name, class, and section (uploaded through "Lets Play with Art")
        const studentName = data.metadata?.artistName || 
                           data.metadata?.artworkMeta?.artist ||
                           data.metadata?.studentName ||
                           '';
        
        const className = data.metadata?.className || '';
        const section = data.metadata?.section || '';
        
        // Skip if no student name (not tagged to a student)
        if (!studentName || studentName.trim() === '' || studentName === 'Unknown Artist' || studentName.toLowerCase() === 'unknown') {
          console.log('Skipping activity without student name:', doc.id);
          return;
        }
        
        // Skip if no class or section (not properly tagged)
        if (!className || !section) {
          console.log('Skipping activity without class/section:', doc.id, 'className:', className, 'section:', section);
          return;
        }
        
        // Get the original uploaded artwork URL (not transformed)
        const artworkUrl = data.originalImageUrl || 
                          data.metadata?.originalImageUrl ||
                          '';
        
        // Skip if no artwork URL
        if (!artworkUrl) {
          console.log('Skipping activity with no original image URL:', doc.id);
          return;
        }
        
        // Skip blob URLs (they're temporary)
        if (artworkUrl.startsWith('blob:')) {
          console.log('Skipping blob URL:', doc.id);
          return;
        }
        
        // Skip data URLs that are too large (they won't work well for sharing)
        if (artworkUrl.startsWith('data:image') && artworkUrl.length > 1000000) {
          console.warn('Skipping large data URL artwork:', doc.id);
          return;
        }

        // Extract artwork metadata
        const artworkTitle = data.title || 
                            data.metadata?.artworkMeta?.title ||
                            'Untitled Artwork';

        // Add all matching activities (don't deduplicate - show all unique activities)
        artworksArray.push({
          id: doc.id,
          artworkUrl: artworkUrl,
          title: artworkTitle,
          artistName: studentName,
          className: className,
          section: section,
          createdAt: data.createdAt,
          metadata: data.metadata || {}
        });
      });

      // Sort by creation date (newest first)
      artworksArray.sort((a, b) => {
        const aDate = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(a.createdAt || 0);
        const bDate = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt || 0);
        return bDate - aDate;
      });
      
      console.log('✅ Fetched', artworksArray.length, 'unique artworks for sharing');
      setAllArtworks(artworksArray);
      
      if (artworksArray.length > 0) {
        setCurrentArtworkIndex(0);
        const firstArtwork = artworksArray[0];
        setSelectedArtworkForShare(firstArtwork);
        // Pre-populate form with existing data
        setShareFormData({
          studentName: firstArtwork.artistName !== 'Unknown Artist' ? firstArtwork.artistName : '',
          className: firstArtwork.className || '',
          section: firstArtwork.section || '',
          schoolName: '',
          mentorComments: ''
        });
      } else {
        setSelectedArtworkForShare(null);
        console.warn('⚠️ No artworks found to share');
      }
    } catch (error) {
      console.error('❌ Error fetching artworks:', error);
      setAllArtworks([]);
      setSelectedArtworkForShare(null);
    }
  };

  // Load artworks when share modal opens
  useEffect(() => {
    const mentorId = getMentorId();
    if (showShareModal && mentorId) {
      fetchAllArtworks();
    }
  }, [showShareModal, user?.uid, hasMentorSession()]);

  // Fetch mentor activities
  const fetchMentorActivities = async () => {
    const mentorId = getMentorId();
    if (!mentorId) {
      console.warn('No mentor ID available for fetching activities');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      
      // Base mentor query - use UID if available, otherwise use email
      let activitiesQueryRef;
      if (user?.uid) {
        activitiesQueryRef = query(
          collection(db, 'userActivities'),
          where('metadata.context', '==', 'mentor'),
          where('metadata.mentorId', '==', user.uid)
        );
      } else {
        // For mentor_session, query by mentorEmail
        activitiesQueryRef = query(
          collection(db, 'userActivities'),
          where('metadata.context', '==', 'mentor'),
          where('metadata.mentorEmail', '==', mentorId.toLowerCase())
        );
      }

      // Apply class and section filters client-side for now (to avoid index requirements)

      const snapshot = await getDocs(activitiesQueryRef);
      const mentorActivities = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Backfill missing imageHash for existing docs with large URLs
      try {
        const updates = [];
        mentorActivities.forEach(item => {
          const url = item.originalImageUrl;
          const needsHash = url && typeof url === 'string' && url.length > 300 && !item.metadata?.imageHash;
          if (needsHash) {
            let h = 0;
            for (let i = 0; i < url.length; i++) {
              h = (h * 31 + url.charCodeAt(i)) >>> 0;
            }
            const imageHash = h.toString(16);
            updates.push(updateDoc(doc(db, 'userActivities', item.id), {
              metadata: { ...(item.metadata || {}), imageHash }
            }));
          }
        });
        if (updates.length) {
          await Promise.allSettled(updates);
          console.log('✅ Backfilled imageHash for', updates.length, 'activities');
        }
      } catch (e) {
        console.warn('⚠️ Backfill imageHash failed:', e?.message || e);
      }

      // Sort client-side by createdAt desc
      mentorActivities.sort((a, b) => {
        const ad = a.createdAt?.toDate ? a.createdAt.toDate() : a.createdAt;
        const bd = b.createdAt?.toDate ? b.createdAt.toDate() : b.createdAt;
        return new Date(bd) - new Date(ad);
      });

      const filtered = mentorActivities.filter(a => {
        const c = a.metadata?.className || '';
        const s = a.metadata?.section || '';
        const classOk = !classFilter || c === classFilter;
        const sectionOk = !sectionFilter || s === sectionFilter;
        return classOk && sectionOk;
      });

      // Group activities by artwork (imageHash preferred, fallback to normalized originalImageUrl)
      const artworkGroups = {};
      filtered.forEach(activity => {
        const normalizeUrl = (url) => {
          if (!url || typeof url !== 'string') return url;
          const q = url.indexOf('?');
          return q >= 0 ? url.slice(0, q) : url;
        };
        const normalizedOriginal = normalizeUrl(activity.originalImageUrl);
        // Derive a stable hash for oversized data URLs when hashes are missing
        const deriveHashIfNeeded = (url) => {
          if (!url || typeof url !== 'string') return null;
          if (url.length <= 300) return null;
          let h = 0;
          for (let i = 0; i < url.length; i++) {
            h = (h * 31 + url.charCodeAt(i)) >>> 0;
          }
          return h.toString(16);
        };
        
        // Special grouping for art history activities - group all art history appreciations and comparisons into one card
        let featureValue = activity.feature || activity.metadata?.feature || null;
        const isArtHistoryAppreciation = featureValue === 'art-history-appreciation';
        const isArtComparison = featureValue === 'art-comparison';
        const isArtHistoryActivity = isArtHistoryAppreciation || isArtComparison;
        
        // Use a fixed groupKey for all art history activities (appreciations and comparisons)
        const groupKey = isArtHistoryActivity 
          ? 'art-history-group' 
          : (activity.metadata?.imageHash || activity.imageHash || deriveHashIfNeeded(normalizedOriginal || activity.originalImageUrl) || normalizedOriginal);
        const artworkUrl = normalizedOriginal || activity.originalImageUrl;
        if (!artworkGroups[groupKey]) {
          artworkGroups[groupKey] = {
            artworkUrl: isArtHistoryActivity ? null : artworkUrl, // No single artwork for grouped art history
            title: isArtHistoryActivity ? 'Art History' : (activity.title || 'Untitled Artwork'),
            artist: isArtHistoryActivity ? 'Multiple Artists' : (activity.metadata?.artistName || 'Unknown Artist'),
            className: activity.metadata?.className || '',
            section: activity.metadata?.section || '',
            createdAt: activity.createdAt,
            activities: [],
            hasReimagineVariant: false,
            isArtHistoryGroup: isArtHistoryActivity
          };
        }
        if (!featureValue && activity.transformedImageUrl) featureValue = 'reimagine';
        if (!featureValue) featureValue = 'unknown';
        // Normalize reimagine variants for legacy/new data
        const isReimagineVariant = (
          featureValue === 'reimagine' ||
          featureValue === 'reimagine-with-feedback' ||
          featureValue === 'reimagine-artwork' ||
          (!!activity.transformedImageUrl)
        );
        if (isReimagineVariant) {
          artworkGroups[groupKey].hasReimagineVariant = true;
        }

        artworkGroups[groupKey].activities.push({
          id: activity.id,
          type: activity.type,
          feature: featureValue,
          createdAt: activity.createdAt,
          title: activity.title,
          isCompleted: activity.isCompleted || activity.status === 'completed',
          transformedImageUrl: activity.transformedImageUrl,
          metadata: activity.metadata
        });
        
        // Update createdAt to the most recent activity for art history group
        if (isArtHistoryActivity) {
          const currentDate = artworkGroups[groupKey].createdAt?.toDate ? artworkGroups[groupKey].createdAt.toDate() : new Date(artworkGroups[groupKey].createdAt);
          const activityDate = activity.createdAt?.toDate ? activity.createdAt.toDate() : new Date(activity.createdAt);
          if (activityDate > currentDate) {
            artworkGroups[groupKey].createdAt = activity.createdAt;
          }
        }
      });


      // Convert to array and sort by most recent activity
      const groupedActivities = Object.values(artworkGroups).sort((a, b) => 
        new Date(b.createdAt?.toDate ? b.createdAt.toDate() : b.createdAt) - 
        new Date(a.createdAt?.toDate ? a.createdAt.toDate() : a.createdAt)
      );

      setActivities(groupedActivities);
      
      // Calculate stats from all activities (not grouped)
      const completedCount = filtered.filter(activity => 
        activity.isCompleted || activity.status === 'completed'
      ).length;
      
      const featureCounts = {};
      filtered.forEach(activity => {
        const feature = activity.metadata?.feature || 'unknown';
        featureCounts[feature] = (featureCounts[feature] || 0) + 1;
      });

      setStats({
        totalActivities: filtered.length,
        completedActivities: completedCount,
        features: featureCounts
      });

      // Fetch ways of seeing activities for each artwork
      const waysOfSeeingMap = await fetchWaysOfSeeingActivities(groupedActivities);

      // Resolve original artwork URLs for all artwork groups (pass waysOfSeeingMap directly)
      await resolveArtworkUrls(groupedActivities, waysOfSeeingMap);

    } catch (error) {
      console.error('Error fetching mentor activities:', error);
    } finally {
      setLoading(false);
    }
  };

  // Resolve original artwork URLs for all artwork groups
  const resolveArtworkUrls = async (artworkGroups = [], waysOfSeeingMap = {}) => {
    const resolvedUrls = {};
    
    for (const artworkGroup of artworkGroups) {
      const groupKey = getArtworkGroupKey(artworkGroup);
      if (!groupKey) continue;
      
      // Skip art history groups (they don't have a single original artwork)
      if (artworkGroup.isArtHistoryGroup) {
        continue;
      }
      
      // Collect all possible original artwork URLs
      const candidateUrls = [
        artworkGroup.artworkUrl,
        ...(artworkGroup.activities?.map(act => 
          act.originalImageUrl || 
          act.metadata?.originalImageUrl ||
          act.originalArtworkUrl ||
          act.metadata?.originalArtworkUrl
        ).filter(Boolean) || [])
      ];
      
      // Also check ways of seeing activities for this group (use passed map or state)
      const waysOfSeeing = waysOfSeeingMap[groupKey] || waysOfSeeingActivities[groupKey] || [];
      for (const activity of waysOfSeeing) {
        const originalUrl = activity.originalImageUrl || 
                           activity.originalArtworkUrl ||
                           activity.metadata?.originalImageUrl ||
                           activity.metadata?.originalArtworkUrl;
        if (originalUrl) {
          candidateUrls.push(originalUrl);
        }
      }
      
      // Resolve the first valid URL
      const resolvedUrl = await resolveFirstValidUrl(...candidateUrls);
      if (resolvedUrl) {
        resolvedUrls[groupKey] = resolvedUrl;
        console.log('✅ Resolved original artwork URL for group:', groupKey, resolvedUrl.substring(0, 100));
      } else {
        console.warn('⚠️ Could not resolve original artwork URL for group:', groupKey, 'candidate URLs:', candidateUrls.length);
      }
    }
    
    setResolvedArtworkUrls(resolvedUrls);
  };

  // Fetch ways of seeing activities (style transformations) for each artwork
  const fetchWaysOfSeeingActivities = async (artworkGroups = []) => {
    const mentorId = getMentorId();
    if (!mentorId) return {};

    try {
      const waysOfSeeingMap = {};
      
      // For each artwork group, fetch all style transformation activities (ways of seeing)
      for (const artworkGroup of artworkGroups) {
        const groupKey = getArtworkGroupKey(artworkGroup);
        if (!groupKey) continue;
        
        try {
          const candidateImageHashes = new Set();
          const candidateOriginalUrls = new Set();

          if (artworkGroup.artworkUrl) candidateOriginalUrls.add(artworkGroup.artworkUrl);
          artworkGroup.activities?.forEach(act => {
            const hash = act.metadata?.imageHash || act.imageHash;
            if (hash) candidateImageHashes.add(hash);
            if (act.originalImageUrl) candidateOriginalUrls.add(act.originalImageUrl);
            if (act.metadata?.originalImageUrl) candidateOriginalUrls.add(act.metadata.originalImageUrl);
          });
          
          const allActivities = [];

          const fetchSnapshots = async (queryRef) => {
            const snapshot = await getDocs(queryRef);
            snapshot.docs.forEach(doc => {
              allActivities.push({ id: doc.id, ...doc.data() });
            });
          };

          if (candidateImageHashes.size > 0) {
            for (const hash of candidateImageHashes) {
              try {
                let hashQuery;
                if (user?.uid) {
                  hashQuery = query(
                    collection(db, 'userActivities'),
                    where('userId', '==', user.uid),
                    where('type', '==', 'reimagine'),
                    where('metadata.imageHash', '==', hash)
                  );
                } else {
                  // For mentor_session, query by mentorEmail
                  hashQuery = query(
                    collection(db, 'userActivities'),
                    where('metadata.mentorEmail', '==', mentorId.toLowerCase()),
                    where('type', '==', 'reimagine'),
                    where('metadata.imageHash', '==', hash)
                  );
                }
                await fetchSnapshots(hashQuery);
              } catch (hashError) {
                console.warn('Failed hash query for ways of seeing:', hashError?.message || hashError);
              }
            }
          }

          if (allActivities.length === 0 && candidateOriginalUrls.size > 0) {
            for (const url of candidateOriginalUrls) {
              let waysOfSeeingQuery;
              if (user?.uid) {
                waysOfSeeingQuery = query(
                  collection(db, 'userActivities'),
                  where('userId', '==', user.uid),
                  where('type', '==', 'reimagine'),
                  where('originalImageUrl', '==', url)
                );
              } else {
                // For mentor_session, query by mentorEmail
                waysOfSeeingQuery = query(
                  collection(db, 'userActivities'),
                  where('metadata.mentorEmail', '==', mentorId.toLowerCase()),
                  where('type', '==', 'reimagine'),
                  where('originalImageUrl', '==', url)
                );
              }
              await fetchSnapshots(waysOfSeeingQuery);
            }
          }

          // Deduplicate by document ID
          const uniqueActivities = Object.values(allActivities.reduce((acc, activity) => {
            if (activity?.id) {
              acc[activity.id] = activity;
            }
            return acc;
          }, {}));
          
          // Filter to only include style transformations (ways of seeing)
          // These are activities with styleInfo and transformedImageUrl
          // Include ways-of-seeing-journey and exclude reimagine-artwork/reimagine-with-feedback
          const waysOfSeeing = uniqueActivities.filter(activity => {
            const derivedStyleInfo = buildStyleInfo(activity);
            const transformedUrl = activity.transformedImageUrl ||
              activity.imageUrl ||
              (activity.journey && activity.journey.length > 0 ? activity.journey[activity.journey.length - 1].transformedImageUrl : null);

            // Must have style info and transformed image
            if (!derivedStyleInfo || !transformedUrl) {
              return false;
            }
            
            // Check the feature to determine if it's a ways of seeing activity
            const feature = activity.feature || activity.metadata?.feature || '';
            
            // Include ways-of-seeing-journey (these are style transformations)
            if (feature === 'ways-of-seeing-journey') {
              return true;
            }
            
            // Exclude only specific reimagine activities that are NOT style transformations
            // reimagine-artwork and reimagine-with-feedback are the "Re-imagine Artwork" feature
            // Regular reimagine with styleInfo is a style transformation (ways of seeing)
            const isReimagineArtwork = feature === 'reimagine-artwork' || 
                                      feature === 'reimagine-with-feedback';
            
            // Include if it has styleInfo (style transformation) and is not a reimagine-artwork activity
            // Style transformations from art-world page typically have styleInfo and may have feature='reimagine' or no feature
            // The key is: if it has styleInfo, it's a ways of seeing activity (unless it's explicitly reimagine-artwork)
            return !isReimagineArtwork && derivedStyleInfo?.name;
          }).map(activity => {
            const builtStyleInfo = buildStyleInfo(activity);
            const existingStyleInfo = activity.styleInfo || activity.metadata?.styleInfo;
            
            // Merge built styleInfo with existing styleInfo to preserve all OpenAI-generated fields
            // Priority: existing styleInfo (has OpenAI data) > builtStyleInfo (from metadata)
            const styleInfo = existingStyleInfo 
              ? { ...builtStyleInfo, ...existingStyleInfo } // Merge, with existing taking precedence
              : builtStyleInfo;
            
            const transformedUrl = activity.transformedImageUrl ||
              activity.imageUrl ||
              (activity.journey && activity.journey.length > 0 ? activity.journey[activity.journey.length - 1].transformedImageUrl : null);

            return {
              ...activity,
              styleInfo: styleInfo || null,
              transformedImageUrl: transformedUrl,
              metadata: {
                ...(activity.metadata || {}),
                styleInfo: styleInfo || activity.metadata?.styleInfo
              }
            };
          });

          const dedupedWays = dedupeWaysOfSeeingActivitiesByArtworkStyle(waysOfSeeing);

          if (dedupedWays.length > 0) {
            waysOfSeeingMap[groupKey] = dedupedWays;
          }
        } catch (error) {
          console.error(`Error fetching ways of seeing for artwork ${artworkGroup.artworkUrl}:`, error);
        }
      }
      
      setWaysOfSeeingActivities(waysOfSeeingMap);
      return waysOfSeeingMap;
    } catch (error) {
      console.error('Error fetching ways of seeing activities:', error);
      return {};
    }
  };

  // Fetch organization stats if user has orgId
  const fetchOrgStats = async () => {
    if (!user?.orgId) return;

    try {
      const orgStatsRef = doc(db, 'orgActivityStats', user.orgId);
      const orgStatsDoc = await getDoc(orgStatsRef);
      
      if (orgStatsDoc.exists()) {
        setOrgStats(orgStatsDoc.data());
      }
    } catch (error) {
      console.error('Error fetching org stats:', error);
    }
  };

  useEffect(() => {
    if (isAuthenticated && user) {
      fetchMentorActivities();
      fetchOrgStats();
    }
  }, [isAuthenticated, user, classFilter, sectionFilter]);

  // Real-time updates
  useEffect(() => {
    if (!user?.uid) return;

    const activitiesQuery = query(
      collection(db, 'userActivities'),
      where('metadata.context', '==', 'mentor'),
      where('metadata.mentorId', '==', user.uid)
    );

    const unsubscribe = onSnapshot(activitiesQuery, (snapshot) => {
      const mentorActivities = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Sort client-side by createdAt desc
      mentorActivities.sort((a, b) => {
        const ad = a.createdAt?.toDate ? a.createdAt.toDate() : a.createdAt;
        const bd = b.createdAt?.toDate ? b.createdAt.toDate() : b.createdAt;
        return new Date(bd) - new Date(ad);
      });

      const filtered = mentorActivities.filter(a => {
        const c = a.metadata?.className || '';
        const s = a.metadata?.section || '';
        const classOk = !classFilter || c === classFilter;
        const sectionOk = !sectionFilter || s === sectionFilter;
        return classOk && sectionOk;
      });

      // Group activities by artwork (prefer image hash; fallback to originalImageUrl)
      const artworkGroups = {};
      filtered.forEach(activity => {
        const normalizeUrl = (url) => {
          if (!url || typeof url !== 'string') return url;
          const q = url.indexOf('?');
          return q >= 0 ? url.slice(0, q) : url;
        };
        const normalizedOriginal = normalizeUrl(activity.originalImageUrl);
        const deriveHashIfNeeded = (url) => {
          if (!url || typeof url !== 'string') return null;
          if (url.length <= 300) return null;
          let h = 0;
          for (let i = 0; i < url.length; i++) {
            h = (h * 31 + url.charCodeAt(i)) >>> 0;
          }
          return h.toString(16);
        };
        // Special grouping for art history activities - group all art history appreciations and comparisons into one card
        const featureValue = activity.feature || activity.metadata?.feature || null;
        const isArtHistoryAppreciation = featureValue === 'art-history-appreciation';
        const isArtComparison = featureValue === 'art-comparison';
        const isArtHistoryActivity = isArtHistoryAppreciation || isArtComparison;
        
        // Use a fixed groupKey for all art history activities (appreciations and comparisons)
        const groupKey = isArtHistoryActivity 
          ? 'art-history-group' 
          : (activity.metadata?.imageHash || activity.imageHash || deriveHashIfNeeded(normalizedOriginal || activity.originalImageUrl) || normalizedOriginal || activity.originalImageUrl);
        const artworkUrl = activity.originalImageUrl;
        if (!artworkGroups[groupKey]) {
          artworkGroups[groupKey] = {
            artworkUrl: isArtHistoryActivity ? null : artworkUrl, // No single artwork for grouped art history
            title: isArtHistoryActivity ? 'Art History' : (activity.title || 'Untitled Artwork'),
            artist: isArtHistoryActivity ? 'Multiple Artists' : (activity.metadata?.artistName || 'Unknown Artist'),
            className: activity.metadata?.className || '',
            section: activity.metadata?.section || '',
            createdAt: activity.createdAt,
            activities: [],
            isArtHistoryGroup: isArtHistoryActivity
          };
        }
        if (!featureValue && activity.transformedImageUrl) featureValue = 'reimagine';
        if (!featureValue) featureValue = 'unknown';
        artworkGroups[groupKey].activities.push({
          id: activity.id,
          type: activity.type,
          feature: featureValue,
          createdAt: activity.createdAt,
          title: activity.title,
          isCompleted: activity.isCompleted || activity.status === 'completed',
          transformedImageUrl: activity.transformedImageUrl,
          metadata: activity.metadata
        });
        
        // Update createdAt to the most recent activity for art history group
        if (isArtHistoryActivity) {
          const currentDate = artworkGroups[groupKey].createdAt?.toDate ? artworkGroups[groupKey].createdAt.toDate() : new Date(artworkGroups[groupKey].createdAt);
          const activityDate = activity.createdAt?.toDate ? activity.createdAt.toDate() : new Date(activity.createdAt);
          if (activityDate > currentDate) {
            artworkGroups[groupKey].createdAt = activity.createdAt;
          }
        }
      });


      // Convert to array and sort by most recent activity
      const groupedActivities = Object.values(artworkGroups).sort((a, b) => 
        new Date(b.createdAt?.toDate ? b.createdAt.toDate() : b.createdAt) - 
        new Date(a.createdAt?.toDate ? a.createdAt.toDate() : a.createdAt)
      );

      setActivities(groupedActivities);
      
      // Update stats from all activities (not grouped)
      const completedCount = filtered.filter(activity => 
        activity.isCompleted || activity.status === 'completed'
      ).length;
      
      const featureCounts = {};
      filtered.forEach(activity => {
        const feature = activity.metadata?.feature || 'unknown';
        featureCounts[feature] = (featureCounts[feature] || 0) + 1;
      });

      setStats({
        totalActivities: filtered.length,
        completedActivities: completedCount,
        features: featureCounts
      });
      
      // Fetch ways of seeing activities for each artwork and resolve URLs
      fetchWaysOfSeeingActivities(groupedActivities).then(waysOfSeeingMap => {
        resolveArtworkUrls(groupedActivities, waysOfSeeingMap);
      });
    }, undefined, { purpose: 'MentorActivityDashboard-activities', component: 'MentorActivityDashboard' });

    return () => unsubscribe();
  }, [user?.uid, classFilter, sectionFilter]);

  // Resolve original image URL when modal opens (for gs:// URLs that weren't resolved earlier)
  useEffect(() => {
    const resolveOriginalImage = async () => {
      if (!showDetailsModal || !selectedActivity) {
        setResolvedOriginalImageUrl(null);
        return;
      }

      // Get the current original image URL
      const currentUrl = selectedActivity.originalImageUrl || 
                        selectedActivity.artworkUrl || 
                        selectedArtworkGroup?.artworkUrl;

      if (!currentUrl) {
        setResolvedOriginalImageUrl(null);
        return;
      }

      // If it's already a valid URL (not gs:// or blob), use it directly
      if (!currentUrl.startsWith('gs://') && !currentUrl.startsWith('blob:')) {
        setResolvedOriginalImageUrl(currentUrl);
        return;
      }

      // If it's a gs:// URL, resolve it
      if (currentUrl.startsWith('gs://')) {
        try {
          const storage = getStorage();
          const fileRef = storageRef(storage, currentUrl);
          const resolvedUrl = await getDownloadURL(fileRef);
          setResolvedOriginalImageUrl(resolvedUrl);
          console.log('✅ Resolved gs:// URL in modal:', currentUrl, '->', resolvedUrl);
        } catch (error) {
          console.error('❌ Failed to resolve gs:// URL in modal:', currentUrl, error);
          setResolvedOriginalImageUrl(null);
        }
      } else {
        // For blob URLs or other cases, try to use it or fallback
        setResolvedOriginalImageUrl(currentUrl);
      }
    };

    resolveOriginalImage();
  }, [showDetailsModal, selectedActivity, selectedArtworkGroup]);

  const [showDeleteSubModal, setShowDeleteSubModal] = useState(false);
  const [deleteSubTarget, setDeleteSubTarget] = useState(null); // { id, title }

  const requestDeleteActivity = (activity) => {
    setDeleteSubTarget({ id: activity.id, title: activity.title });
    setShowDeleteSubModal(true);
  };

  const handleDeleteActivity = async () => {
    if (!deleteSubTarget?.id) return;
    try {
      await deleteDoc(doc(db, 'userActivities', deleteSubTarget.id));
      console.log('Activity deleted successfully');
    } catch (error) {
      console.error('Error deleting activity:', error);
    } finally {
      setShowDeleteSubModal(false);
      setDeleteSubTarget(null);
    }
  };

  // Bulk delete all sub-activities for a given artwork group
  const openDeleteArtworkGroup = (group) => {
    setDeleteTarget({ 
      artworkUrl: group.artworkUrl, 
      title: group.title,
      isArtHistoryGroup: group.isArtHistoryGroup || false
    });
    setShowDeleteModal(true);
  };

  const handleDeleteArtworkGroup = async () => {
    const mentorId = getMentorId();
    if (!mentorId || !deleteTarget) return;
    try {
      const artworkUrl = deleteTarget.artworkUrl;
      let qRef;
      
      // Build query based on whether we have Firebase user or mentor_session
      const buildQuery = (additionalWhere) => {
        if (user?.uid) {
          return query(
            collection(db, 'userActivities'),
            where('metadata.context', '==', 'mentor'),
            where('metadata.mentorId', '==', user.uid),
            ...additionalWhere
          );
        } else {
          return query(
            collection(db, 'userActivities'),
            where('metadata.context', '==', 'mentor'),
            where('metadata.mentorEmail', '==', mentorId.toLowerCase()),
            ...additionalWhere
          );
        }
      };
      
      // Special handling for art history appreciation group
      if (deleteTarget.isArtHistoryGroup) {
        // Delete both art history appreciations and comparisons
        qRef = buildQuery([
          where('metadata.feature', 'in', ['art-history-appreciation', 'art-comparison'])
        ]);
      } else if (typeof artworkUrl === 'string' && artworkUrl.length > 300) {
        // If URL is too large for Firestore query, use imageHash fallback
        let h = 0;
        for (let i = 0; i < artworkUrl.length; i++) {
          h = (h * 31 + artworkUrl.charCodeAt(i)) >>> 0;
        }
        const imageHash = h.toString(16);
        qRef = buildQuery([
          where('metadata.imageHash', '==', imageHash)
        ]);
      } else {
        qRef = buildQuery([
          where('originalImageUrl', '==', artworkUrl)
        ]);
      }
      const snap = await getDocs(qRef);
      const deletions = snap.docs.map(d => deleteDoc(doc(db, 'userActivities', d.id)));
      await Promise.allSettled(deletions);
      console.log('🗑️ Deleted artwork group for url/hash target:', deleteTarget.artworkUrl || 'art-history-group', 'count:', snap.size);
      setShowDeleteModal(false);
      setDeleteTarget(null);
      // Refresh list
      fetchMentorActivities();
    } catch (e) {
      console.error('Failed to delete artwork group:', e);
      setShowDeleteModal(false);
      setDeleteTarget(null);
    }
  };

  const handleViewDetails = async (activity, artworkGroup = null) => {
    let target = activity;
    if (activity.activities) {
      target = activity.activities[0];
    }
    
    try {
      let finalOriginalUrl = null;
      
      if (target?.id) {
        const docRef = doc(db, 'userActivities', target.id);
        const snap = await getDoc(docRef);
        if (snap.exists()) {
          const firestoreData = snap.data();
          
          // For ways-of-seeing-journey, the original artwork might be in the first journey step
          let journeyOriginalUrl = null;
          if (firestoreData.journey && Array.isArray(firestoreData.journey) && firestoreData.journey.length > 0) {
            // Check if first journey step has original artwork reference
            const firstStep = firestoreData.journey[0];
            // The original artwork URL should be at the activity level, not in journey steps
            // But we can use it as a fallback
          }
          
          // For ways-of-seeing activities, prioritize artworkGroup's artworkUrl as it should have the original
          // Try to resolve a valid URL from various sources, prioritizing artworkGroup first for ways-of-seeing
          const feature = firestoreData.metadata?.feature || firestoreData.feature || '';
          const isWaysOfSeeing = feature === 'ways-of-seeing-journey' || 
                                (firestoreData.styleInfo && firestoreData.transformedImageUrl);
          
          if (isWaysOfSeeing && artworkGroup?.artworkUrl) {
            // For ways of seeing, the artworkGroup should have the original artwork URL
            finalOriginalUrl = await resolveFirstValidUrl(
              artworkGroup.artworkUrl,
            firestoreData.originalImageUrl,
              firestoreData.originalArtworkUrl,
              firestoreData.metadata?.originalImageUrl,
              firestoreData.metadata?.originalArtworkUrl,
              activity.artworkUrl,
              target?.originalImageUrl,
              target?.originalArtworkUrl,
              activity.activities?.[0]?.originalImageUrl,
              activity.activities?.[0]?.originalArtworkUrl
            );
          } else {
            // For other activities, prioritize Firestore data
            finalOriginalUrl = await resolveFirstValidUrl(
              firestoreData.originalImageUrl,
              firestoreData.originalArtworkUrl,
              firestoreData.metadata?.originalImageUrl,
              firestoreData.metadata?.originalArtworkUrl,
            artworkGroup?.artworkUrl,
            activity.artworkUrl,
            target?.originalImageUrl,
              target?.originalArtworkUrl,
              activity.activities?.[0]?.originalImageUrl,
              activity.activities?.[0]?.originalArtworkUrl
          );
          }
          
          // If all URLs are blob URLs, try to find a valid URL from other activities in the group
          if (!finalOriginalUrl && artworkGroup?.activities && artworkGroup.activities.length > 0) {
            for (const groupActivity of artworkGroup.activities) {
              const groupUrl = await resolveFirstValidUrl(
                groupActivity.originalImageUrl,
                groupActivity.originalArtworkUrl,
                groupActivity.metadata?.originalImageUrl,
                groupActivity.metadata?.originalArtworkUrl
              );
              if (groupUrl) {
                finalOriginalUrl = groupUrl;
                break;
              }
            }
          }
          
          // If still no valid URL, check if Firestore has a base64 data URL (which is valid)
          if (!finalOriginalUrl && firestoreData.originalImageUrl?.startsWith('data:image/')) {
              finalOriginalUrl = firestoreData.originalImageUrl;
          }
          
          console.log('🔍 handleViewDetails - URL sources:', {
            feature: feature,
            isWaysOfSeeing: isWaysOfSeeing,
            firestoreUrl: firestoreData.originalImageUrl,
            firestoreOriginalArtworkUrl: firestoreData.originalArtworkUrl,
            artworkGroupUrl: artworkGroup?.artworkUrl,
            activityArtworkUrl: activity.artworkUrl,
            targetOriginalUrl: target?.originalImageUrl,
            firstActivityUrl: activity.activities?.[0]?.originalImageUrl,
            finalOriginalUrl: finalOriginalUrl,
            isBlob: isBlobUrl(finalOriginalUrl),
            isDataUrl: finalOriginalUrl?.startsWith('data:image/'),
            isGsUrl: finalOriginalUrl?.startsWith('gs://'),
            hasStyleInfo: !!firestoreData.styleInfo,
            hasTransformedImage: !!firestoreData.transformedImageUrl
          });
          
          setSelectedActivity({ 
            id: snap.id, 
            ...firestoreData,
            // Ensure originalImageUrl is set - use resolved URL if available, or data URL, or fallback to blob
            originalImageUrl: finalOriginalUrl || firestoreData.originalImageUrl || target.originalImageUrl,
            // Ensure transformedImageUrl and metadata are included
            transformedImageUrl: firestoreData.transformedImageUrl || target.transformedImageUrl,
            metadata: firestoreData.metadata || target.metadata,
            // Ensure feature is preserved
            feature: firestoreData.metadata?.feature || target.feature,
            // Store artworkUrl from group for reference (but prefer resolved non-blob)
            artworkUrl: finalOriginalUrl || (artworkGroup?.artworkUrl) || activity.artworkUrl
          });
          // Store artworkGroup for fallback in modal
          setSelectedArtworkGroup(artworkGroup);
        } else {
          // Document doesn't exist, try to get non-blob URL from available sources
          finalOriginalUrl = await resolveFirstValidUrl(
            artworkGroup?.artworkUrl,
            activity.artworkUrl,
            target?.originalImageUrl,
            target?.originalArtworkUrl,
            activity.activities?.[0]?.originalImageUrl,
            activity.activities?.[0]?.originalArtworkUrl
          );
          
          setSelectedActivity({
            ...target,
            originalImageUrl: finalOriginalUrl || target.originalImageUrl,
            artworkUrl: finalOriginalUrl || (artworkGroup?.artworkUrl) || activity.artworkUrl
          });
          setSelectedArtworkGroup(artworkGroup);
        }
      } else {
        // No ID, try to get non-blob URL from available sources
        finalOriginalUrl = await resolveFirstValidUrl(
          artworkGroup?.artworkUrl,
          activity.artworkUrl,
          target?.originalImageUrl,
          target?.originalArtworkUrl,
          activity.activities?.[0]?.originalImageUrl,
          activity.activities?.[0]?.originalArtworkUrl
        );
        
        setSelectedActivity({
          ...target,
          originalImageUrl: finalOriginalUrl || target.originalImageUrl,
          artworkUrl: finalOriginalUrl || (artworkGroup?.artworkUrl) || activity.artworkUrl
        });
        setSelectedArtworkGroup(artworkGroup);
      }
      
      // Collapse all sections initially on open
      setDetailsOpen({ appreciation: false, techniques: false, movements: false, concepts: false });
      setSelectedPointIdx(null);
    } catch (e) {
      console.warn('Failed to load full activity details:', e);
      // On error, try to get non-blob URL from available sources
      const finalOriginalUrl = await resolveFirstValidUrl(
        artworkGroup?.artworkUrl,
        activity.artworkUrl,
        target?.originalImageUrl,
        target?.originalArtworkUrl,
        activity.activities?.[0]?.originalImageUrl,
        activity.activities?.[0]?.originalArtworkUrl
      );
      
      setSelectedActivity({
        ...target,
        originalImageUrl: finalOriginalUrl || target.originalImageUrl,
        artworkUrl: finalOriginalUrl || (artworkGroup?.artworkUrl) || activity.artworkUrl
      });
      setSelectedArtworkGroup(artworkGroup);
    }
    setShowDetailsModal(true);
  };

  // Handle sharing artwork
  const handleShareArtwork = async () => {
    const mentorId = getMentorId();
    if (!selectedArtworkForShare || !mentorId) {
      alert('Please select an artwork to share');
      return;
    }

    if (!shareOptions.artmosphereMentors && !shareOptions.organizationMentors) {
      alert('Please select at least one sharing option');
      return;
    }

    // Check consent settings for artwork sharing
    // Note: This is for student artworks being shared by mentors
    // We need to check the student's consent, not the mentor's
    // If it's a student artwork, we need to check their consent
    if (selectedArtworkForShare.studentId || selectedArtworkForShare.metadata?.studentId || shareFormData.studentName) {
      // Try to find the student ID from the artwork metadata or form data
      let studentId = selectedArtworkForShare.studentId || selectedArtworkForShare.metadata?.studentId;
      
      // If we have student name, try to find the student by name
      if (!studentId && shareFormData.studentName) {
        // For now, we'll check the current user's consent if it's their artwork
        // In a real scenario, you'd look up the student by name
        studentId = user?.uid;
      }
      
      if (studentId) {
        const canShare = await canShareArtworkPublicly(studentId);
        const isUnder13User = await isUnder13(studentId);
        
        if (!canShare) {
          if (isUnder13User) {
            alert('To share this artwork publicly, the student\'s parent needs to enable artwork sharing in Account Settings. An email will be sent to the parent for approval.');
            // TODO: Trigger parent consent email
            return;
          } else {
            alert('To share this artwork publicly, the student needs to allow artwork sharing in Account Settings. Please ask them to enable "Allow my artwork to be shared publicly" in their Account Settings.');
            return;
          }
        }
      }
    }

    try {
      // Generate access code if organization sharing with access code enabled
      let accessCode = null;
      if (shareOptions.organizationMentors && shareOptions.allowAccessCode) {
        accessCode = Math.random().toString(36).substring(2, 10).toUpperCase();
      }

      // Get parentId for organization sharing
      let parentId = null;
      if (shareOptions.organizationMentors) {
        try {
          // For mentor_session, get parentId from session
          if (!user?.uid) {
            const session = JSON.parse(localStorage.getItem('mentor_session') || '{}');
            parentId = session?.ownerId || null;
          } else {
            const userDocRef = doc(db, 'users', user.uid);
            const userDoc = await getDoc(userDocRef);
            if (userDoc.exists()) {
              const userData = userDoc.data();
              parentId = userData.parentId || userData.primaryId || userData.orgId || user.uid;
              // For organization mentors, use primaryId if available
              if (userData.mentorType === 'organization' && !userData.isPrimaryId && userData.primaryId) {
                parentId = userData.primaryId;
              }
            } else {
              parentId = user.uid;
            }
          }
        } catch (error) {
          console.error('Error fetching user document for parentId:', error);
          if (user?.uid) {
            parentId = user.uid;
          } else {
            const session = JSON.parse(localStorage.getItem('mentor_session') || '{}');
            parentId = session?.ownerId || null;
          }
        }
      }

      const shareData = {
        artworkUrl: selectedArtworkForShare.artworkUrl,
        artworkTitle: selectedArtworkForShare.title,
        studentName: shareFormData.studentName,
        className: shareFormData.className,
        section: shareFormData.section,
        schoolName: shareFormData.schoolName,
        mentorComments: shareFormData.mentorComments,
        mentorId: user.uid,
        mentorName: user.displayName || user.email,
        organizationId: user.orgId || null,
        parentId: parentId, // Required for org scope queries
        shareWithArtmosphere: shareOptions.artmosphereMentors,
        shareWithOrganization: shareOptions.organizationMentors,
        accessCode: accessCode,
        createdAt: serverTimestamp(),
        status: 'active'
      };

      // Save to sharedArtworks collection
      await addDoc(collection(db, 'sharedArtworks'), shareData);

      // Show success message
      alert('Artwork shared successfully! 🎨');
      
      // Reset form and close modals
      setShowShareOptionsModal(false);
      setShowShareModal(false);
      setShareFormData({
        studentName: '',
        className: '',
        section: '',
        schoolName: '',
        mentorComments: ''
      });
      
      // Store share options temporarily for redirect logic
      const wasSharedWithArtmosphere = shareOptions.artmosphereMentors;
      const wasSharedWithOrganization = shareOptions.organizationMentors;
      
      setShareOptions({
        artmosphereMentors: false,
        organizationMentors: false,
        allowAccessCode: false
      });

      // If access code was generated, show it to the mentor
      if (accessCode) {
        alert(`Access Code: ${accessCode}\n\nShare this code with others to allow them to view your organization's shared artworks.`);
      }

      // Redirect to learning space based on sharing options
      // If both spaces are chosen, default to global (Artmosphere Co-learning)
      // If only one is chosen, redirect to that selected space
      if (wasSharedWithArtmosphere || wasSharedWithOrganization) {
        // Determine scope based on what was selected
        let scope = 'global'; // Default to global (Artmosphere Co-learning)
        if (wasSharedWithOrganization && !wasSharedWithArtmosphere) {
          scope = 'org'; // Only org selected, go to org learning space
        } else if (wasSharedWithArtmosphere && !wasSharedWithOrganization) {
          scope = 'global'; // Only Artmosphere selected, go to global learning space
        } else {
          // Both selected, default to global (Artmosphere Co-learning)
          scope = 'global';
        }
        
        // Navigate to co-learning space with the appropriate scope
        navigate(`/mentors/co-learning?scope=${scope}`);
      }
    } catch (error) {
      console.error('Error sharing artwork:', error);
      alert('Failed to share artwork. Please try again.');
    }
  };

  const getActivityIcon = (type) => {
    switch (type) {
      case 'appreciation':
        return <FaEye className="w-5 h-5" />;
      case 'story':
        return <FaBook className="w-5 h-5" />;
      case 'reimagine':
        return <FaEdit className="w-5 h-5" />;
      case 'tutorial':
        return <FaGraduationCap className="w-5 h-5" />;
      default:
        return <FaStar className="w-5 h-5" />;
    }
  };

  const getFeatureDisplayName = (feature) => {
    const featureNames = {
      'art-appreciation': 'Art Appreciation',
      'art-history': 'Art History',
      'art-history-appreciation': 'Art History Appreciation',
      'art-compare': 'Art Compare',
      'reimagine-artwork': 'Lets Play with Art.',
      'reimagine': 'Lets Play with Art.',
      'reimagine-with-feedback': 'Lets Play with Art.',
      'art-story': 'Art Story',
      'tutorial': 'Tutorial',
      'ways-of-seeing-journey': 'Ways of Seeing Journey'
    };
    return featureNames[feature] || feature;
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'Unknown date';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Group a list of activities by their feature, with special handling for reimagine activities
  const groupActivitiesByFeature = (activityList) => {
    const featureGroups = {};
    (activityList || []).forEach((a) => {
      let featureKey = a.feature || a.metadata?.feature || 'unknown';
      
      // Group all reimagine variations under 'reimagine'
      if (featureKey === 'reimagine' || featureKey === 'reimagine-with-feedback' || featureKey === 'reimagine-artwork') {
        featureKey = 'reimagine';
      }
      
      if (!featureGroups[featureKey]) featureGroups[featureKey] = [];
      featureGroups[featureKey].push(a);
    });
    return featureGroups;
  };

  useEffect(() => {
    if (!loading) setHasLoadedOnce(true);
  }, [loading]);

  if (loading && !hasLoadedOnce) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-blue-50 to-indigo-100 flex items-center justify-center">
        <ConsistentLoader />
      </div>
    );
  }

  return (
    <MentorAccessCheck>
      <div className="min-h-screen relative">
        {loading && hasLoadedOnce && (
          <div className="fixed top-4 left-1/2 -translate-x-1/2 z-30 bg-white/90 border border-amber-200 text-amber-800 text-xs px-3 py-1.5 rounded-full shadow">
            Refreshing dashboard...
          </div>
        )}
        <KidBackground variant="aurora" intensity={0.6} />
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="relative z-10"
        >
        {/* Refined Header */}
        <header className="sticky top-0 z-20 bg-white/95 backdrop-blur-md border-b border-gray-200/60 shadow-sm">
          <div className="mx-auto max-w-7xl px-6 py-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 bg-gradient-to-br from-amber-500 to-orange-500 rounded-xl flex items-center justify-center shadow-lg">
                  <span className="text-white text-xl font-bold">A</span>
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-gray-900 tracking-tight" style={{ fontFamily: 'Inter, sans-serif' }}>Mentor Activity Dashboard</h1>
                  <p className="text-sm text-gray-600">Your mentoring activities and insights</p>
                </div>
              </div>
              <nav className="flex items-center gap-1 bg-gray-50 rounded-xl p-1">
                <button onClick={()=>navigate('/mentor-dashboard')} className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-gray-900 hover:bg-white rounded-lg transition-all duration-200 text-sm font-medium">Artist Corner</button>
                <button onClick={()=>navigate('/artist-wall')} className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-gray-900 hover:bg-white rounded-lg transition-all duration-200 text-sm font-medium">Collections</button>
              </nav>
            </div>
          </div>
        </header>
        </motion.div>
 
        {/* Content */}
        <div className="max-w-7xl mx-auto px-6 py-10">
          {/* Header Row with Share Button */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
            <div className="flex items-center gap-4">
              <h2 className="text-xl font-bold text-gray-900 tracking-tight">Recent Activities</h2>
              <span className="px-3 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-700">{activities.length} items</span>
            </div>
            <button
              onClick={async () => {
                setShowShareModal(true);
                // Fetch artworks when modal opens
                await fetchAllArtworks();
              }}
              className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-orange-500 to-amber-400 hover:from-orange-600 hover:to-amber-500 text-white rounded-lg text-sm font-semibold transition-all duration-300 shadow-md hover:shadow-lg transform hover:scale-105"
              style={{ fontFamily: 'Baloo 2, Comic Sans MS, cursive' }}
            >
              <FaShare className="w-4 h-4" />
              Share Artwork
            </button>
          </div>
          
          {/* Filters Row */}
          <div className="flex items-center gap-2 mb-8">
              <select value={classFilter} onChange={(e) => { setClassFilter(e.target.value); try { localStorage.setItem('mentorClass', e.target.value); } catch {} }} className="px-3 py-2 border border-gray-200 rounded-full text-sm bg-white hover:border-gray-300">
                <option value="">All Classes</option>
                <option value="Class 1">Class 1</option>
                <option value="Class 2">Class 2</option>
                <option value="Class 3">Class 3</option>
                <option value="Class 4">Class 4</option>
                <option value="Class 5">Class 5</option>
                <option value="Class 6">Class 6</option>
                <option value="Class 7">Class 7</option>
                <option value="Class 8">Class 8</option>
                <option value="Class 9">Class 9</option>
                <option value="Class 10">Class 10</option>
              </select>
              <select value={sectionFilter} onChange={(e) => { setSectionFilter(e.target.value); try { localStorage.setItem('mentorSection', e.target.value); } catch {} }} className="px-3 py-2 border border-gray-200 rounded-full text-sm bg-white hover:border-gray-300">
                <option value="">All Sections</option>
                <option value="A">A</option>
                <option value="B">B</option>
                <option value="C">C</option>
                <option value="D">D</option>
              </select>
            </div>

          {/* Activity Cards */}
          <div className="grid grid-cols-1 gap-5">
            {activities.length === 0 ? (
              <div className="text-center py-16">
                <FaChalkboardTeacher className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500 text-lg">No mentoring activities yet</p>
                <p className="text-gray-400 text-sm">Start by analyzing student artworks to see your activities here</p>
              </div>
            ) : (
              <div className="space-y-8">
                {activities.map((artworkGroup, index) => {
                  // Render Art History Card for art history groups
                  if (artworkGroup.isArtHistoryGroup) {
                    return (
                      <motion.div
                        key={`art-history-group-${index}-${artworkGroup.createdAt?.toDate ? artworkGroup.createdAt.toDate().getTime() : Date.now()}`}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.06 }}
                      >
                        <ArtHistoryCard
                          artworkGroup={artworkGroup}
                          onDeleteActivity={async (activity) => {
                            if (!activity?.id) return;
                            try {
                              await deleteDoc(doc(db, 'userActivities', activity.id));
                              console.log('Art history activity deleted successfully');
                              // Refresh activities
                              fetchMentorActivities();
                            } catch (error) {
                              console.error('Error deleting art history activity:', error);
                            }
                          }}
                        />
                      </motion.div>
                    );
                  }

                  // Regular activity card
                  return (
                  <motion.div
                    key={`artwork-group-${index}-${artworkGroup.title || 'untitled'}-${artworkGroup.createdAt?.toDate ? artworkGroup.createdAt.toDate().getTime() : Date.now()}`}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.06 }}
                    className="bg-white/20 backdrop-blur-sm rounded-2xl shadow-lg overflow-hidden relative border-2 border-white/30"
                  >
                    {/* Amber Artmosphere Aesthetic Strip Label - Full Width */}
                    <div className="absolute -top-3 left-0 right-0 bg-gradient-to-r from-amber-400 via-orange-400 to-yellow-400 text-white py-2 text-center text-sm font-bold shadow-lg z-20 border-2 border-amber-300">
                      Activities of "{artworkGroup.title || 'Untitled Artwork'}"
                    </div>
                    
                    <div className="flex">
                      {/* Artwork on the left */}
                      <div className="w-1/3 p-6">
                        <div className="relative h-64 rounded-xl overflow-hidden border-4 border-amber-200">
                          {(() => {
                            const groupKey = getArtworkGroupKey(artworkGroup);
                            const resolvedUrl = resolvedArtworkUrls[groupKey] || artworkGroup.artworkUrl;
                            
                            if (resolvedUrl) {
                              return (
                                <img
                                  src={resolvedUrl}
                                  alt="Original artwork"
                                  className="w-full h-full object-cover"
                                  onError={(e) => {
                                    console.error('❌ Failed to load original artwork:', resolvedUrl);
                                    e.target.style.display = 'none';
                                    const fallback = e.target.parentElement.querySelector('.artwork-fallback');
                                    if (fallback) fallback.style.display = 'flex';
                                  }}
                                  onLoad={() => {
                                    console.log('✅ Original artwork loaded successfully:', resolvedUrl.substring(0, 100));
                                  }}
                                />
                              );
                            } else {
                              return (
                                <div className="artwork-fallback flex absolute inset-0 items-center justify-center bg-gradient-to-br from-amber-50 to-orange-50">
                                  <div className="text-center">
                                    <FaBook className="w-16 h-16 text-amber-600 mx-auto mb-2" />
                                    <p className="text-sm text-amber-600 font-medium">
                                      {artworkGroup.title || 'Art History Appreciations'}
                                    </p>
                                  </div>
                                </div>
                              );
                            }
                          })()}
                          {/* Fallback when image fails to load */}
                          <div className="artwork-fallback hidden absolute inset-0 items-center justify-center bg-gradient-to-br from-amber-50 to-orange-50">
                            <div className="text-center">
                              <div className="text-4xl mb-2">🎨</div>
                              <p className="text-sm text-amber-600 font-medium">
                                Original Artwork
                              </p>
                              <p className="text-xs text-amber-500 mt-1">
                                {artworkGroup.title || 'Untitled'}
                              </p>
                          </div>
                        </div>
                          <div className="absolute bottom-2 left-2 bg-white/90 px-3 py-1 rounded text-sm font-semibold text-amber-700">
                            Original Artwork
                        </div>
                      </div>
                    </div>
                    
                      {/* Activities strip on the right */}
                      <div className="w-2/3 p-6 pt-8 flex flex-col justify-center min-h-[280px]">
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 mt-2">
                      {(() => {
                        const result = [];
                        
                        // Check if there are ways of seeing activities for this artwork
                        const groupKey = getArtworkGroupKey(artworkGroup);
                        const waysOfSeeing = groupKey ? (waysOfSeeingActivities[groupKey] || []) : [];
                        const hasWaysOfSeeing = waysOfSeeing.length > 0;
                        
                        // Render Ways of Seeing scroller if available
                        if (hasWaysOfSeeing) {
                          result.push(
                            <div key="ways-of-seeing" className="mb-6 last:mb-0">
                              <div className="flex items-center justify-between mb-3">
                                <h6 className="text-sm font-semibold text-gray-800 flex items-center gap-2">
                                  <FaEye className="w-5 h-5" />
                                  <span>Ways of Seeing</span>
                                  <span className="text-xs text-gray-500">({waysOfSeeing.length})</span>
                                </h6>
                              </div>
                              <ReimagineScrollerCard
                                originalArtworkUrl={(() => {
                                  // Use resolved URL if available
                                  const groupKey = getArtworkGroupKey(artworkGroup);
                                  if (groupKey && resolvedArtworkUrls[groupKey]) {
                                    return resolvedArtworkUrls[groupKey];
                                  }
                                  
                                  // Try to find a non-blob URL from the ways of seeing activities
                                  // They should have the original artwork URL in their data
                                  for (const activity of waysOfSeeing) {
                                    const originalUrl = activity.originalImageUrl || 
                                                       activity.originalArtworkUrl ||
                                                       activity.metadata?.originalImageUrl ||
                                                       activity.metadata?.originalArtworkUrl;
                                    if (originalUrl && !isBlobUrl(originalUrl)) {
                                      return originalUrl;
                                    }
                                  }
                                  // Fallback to artworkGroup.artworkUrl (might be blob, but we'll handle it)
                                  return artworkGroup.artworkUrl;
                                })()}
                                originalArtworkTitle={artworkGroup.title}
                                waysOfSeeingActivities={waysOfSeeing}
                                onTutorialClick={async (activity) => {
                                  try {
                                    // Check if tutorial already exists
                                    const { checkTutorialById } = await import('../utils/activityService');
                                    const existingTutorial = await checkTutorialById(
                                      activity.styleInfo?.id,
                                      activity.id
                                    );

                                    // Find the best non-blob URL for the original and transformed artworks
                                    const latestJourneyStep = Array.isArray(activity.journey) && activity.journey.length > 0
                                      ? activity.journey[activity.journey.length - 1]
                                      : null;
                                    const latestTransformation =
                                      Array.isArray(activity.metadata?.transformations) && activity.metadata.transformations.length > 0
                                        ? activity.metadata.transformations[activity.metadata.transformations.length - 1]
                                        : null;

                                    const candidateOriginals = [
                                      activity.metadata?.originalImageUrl,
                                      activity.metadata?.sourceImageUrl,
                                      activity.metadata?.sourceImage?.url,
                                      activity.originalImageUrl,
                                      artworkGroup.artworkUrl
                                    ];
                                    const candidateTransformed = [
                                      activity.transformedImageUrl,
                                      activity.metadata?.transformedImageUrl,
                                      activity.imageUrl,
                                      latestTransformation?.imageUrl,
                                      latestJourneyStep?.transformedImageUrl
                                    ];
                                    
                                    const resolvedOriginal = await resolveFirstValidUrl(...candidateOriginals);
                                    const resolvedTransformed = await resolveFirstValidUrl(...candidateTransformed);

                                    // If tutorial exists, navigate directly to it (skip style selection page)
                                    if (existingTutorial) {
                                      console.log('✅ Tutorial exists, navigating directly to tutorial');
                                      navigate('/art-world/selection', {
                                        state: {
                                          artwork: resolvedOriginal || artworkGroup.artworkUrl,
                                          fromPage: 'mentor-activity-dashboard',
                                          preSelectedStyle: activity.styleInfo,
                                          forceGenerate: false, // Don't force generate if tutorial exists
                                          reimagineActivityId: activity.id,
                                          transformedImage: resolvedTransformed || null,
                                          originalFallback: artworkGroup.artworkUrl,
                                          skipToTutorial: true, // Flag to skip style selection and go directly to tutorial
                                          existingTutorialData: existingTutorial
                                        }
                                      });
                                    } else {
                                      // No tutorial exists, navigate to style selection page for generation
                                      console.log('❌ No tutorial exists, navigating to style selection page');
                                      navigate('/art-world/selection', {
                                        state: {
                                          artwork: resolvedOriginal || artworkGroup.artworkUrl,
                                          fromPage: 'mentor-activity-dashboard',
                                          preSelectedStyle: activity.styleInfo,
                                          forceGenerate: true,
                                          reimagineActivityId: activity.id,
                                          transformedImage: resolvedTransformed || null,
                                          originalFallback: artworkGroup.artworkUrl
                                        }
                                      });
                                    }
                                  } catch (error) {
                                    console.error('Failed to prepare tutorial generation:', error);
                                    alert('Unable to start the tutorial for this artwork. Please try again.');
                                  }
                                }}
                                onCardClick={(activity) => {
                                  handleViewDetails(activity, artworkGroup);
                                }}
                                onDeleteActivity={async (activityId) => {
                                  try {
                                    // Delete the activity
                                    await deleteDoc(doc(db, 'userActivities', activityId));
                                    // The real-time subscription will automatically refresh the activities list
                                    // But we should also refresh ways of seeing activities
                                    await fetchWaysOfSeeingActivities(activities);
                                  } catch (error) {
                                    console.error('Error deleting ways of seeing activity:', error);
                                    alert('Failed to delete activity. Please try again.');
                                  }
                                }}
                              />
                            </div>
                          );
                        }
                        
                        // Filter out ways of seeing activities from regular display since they're shown in the scroller
                        const waysOfSeeingIds = new Set(waysOfSeeing.map(a => a.id));
                        const filteredActivities = artworkGroup.activities.filter(activity => {
                          // Exclude activities that are in the ways of seeing scroller
                          return !waysOfSeeingIds.has(activity.id);
                        });
                        
                        const featureGroups = groupActivitiesByFeature(filteredActivities);
                        const otherActivities = Object.entries(featureGroups).map(([feature, activities]) => {
                          const isReimagine = feature === 'reimagine' || feature === 'reimagine-with-feedback' || feature === 'reimagine-artwork';
                          return (
                            <div key={feature} className="mb-6 last:mb-0">
                              <div className="flex items-center justify-between mb-3">
                                <h6 className="text-sm font-semibold text-gray-800 flex items-center gap-2">
                                  {getActivityIcon(activities[0]?.type)}
                                  <span>{isReimagine ? 'Reimagined Artwork' : getFeatureDisplayName(feature)}</span>
                                  <span className="text-xs text-gray-500">({activities.length})</span>
                                </h6>
                              </div>
                              
                              {isReimagine ? (
                                // Reimagined Artwork Cards matching Magic Studio style
                                activities.flatMap((activity) => {
                                  const items = Array.isArray(activity?.metadata?.transformations) && activity.metadata.transformations.length > 0
                                    ? activity.metadata.transformations.map(t => ({
                                        id: `${activity.id}-${t.createdAt}`,
                                        transformedImageUrl: t.imageUrl,
                                        prompt: t.prompt,
                                        userFeedback: t.userFeedback,
                                        artmosphereFeedback: t.artmosphereFeedback,
                                        createdAt: t.createdAt || activity.createdAt
                                      }))
                                    : [{ 
                                        id: activity.id, 
                                        transformedImageUrl: activity.transformedImageUrl, 
                                        prompt: activity.metadata?.prompt, 
                                        userFeedback: activity.metadata?.userFeedback,
                                        artmosphereFeedback: activity.metadata?.artmosphereFeedback,
                                        createdAt: activity.createdAt 
                                      }];
                                  return items.map((item, itemIndex) => (
                                    <div
                                      key={`${feature}-${activity.id}-${item.id}-${itemIndex}`}
                                      className="bg-gradient-to-br from-orange-100 via-yellow-100 to-pink-100 rounded-xl p-3 cursor-pointer hover:shadow-lg transition-all duration-300 border-2 border-orange-300 hover:border-orange-400 relative overflow-hidden"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleViewDetails(activity, artworkGroup);
                                      }}
                                    >
                                      {/* Fun background pattern */}
                                      <div className="absolute inset-0 opacity-10">
                                        <div className="absolute top-2 right-2 text-4xl rotate-12">🎨</div>
                                        <div className="absolute bottom-2 left-2 text-3xl -rotate-12">✨</div>
                                        <div className="absolute top-1/2 left-1/2 text-2xl transform -translate-x-1/2 -translate-y-1/2 rotate-45">🌈</div>
                                        </div>

                                      {/* Strip Label */}
                                      <div className="absolute -top-1 left-4 bg-gradient-to-r from-orange-400 to-pink-400 text-white px-2 py-0.5 rounded-b text-xs font-bold shadow-md z-20">
                                        Re-imagined Artwork
                                      </div>

                                      {/* Transformed Artwork Only */}
                                      <div className="relative mb-2 z-10 mt-3">
                                        <div className="relative h-28 w-full rounded-2xl overflow-hidden border-2 border-orange-300 bg-gradient-to-br from-yellow-50 to-orange-50 shadow-lg">
                                          <SmartImage
                                            src={item.transformedImageUrl || activity.transformedImageUrl || artworkGroup.artworkUrl || ''} 
                                            alt="Transformed artwork" 
                                            className="w-full h-full object-cover"
                                            containerClassName="w-full h-full"
                                            onError={(e) => {
                                              e.currentTarget.style.display = 'none';
                                              const fallback = e.currentTarget.parentElement.querySelector('.image-fallback');
                                              if (fallback) fallback.style.display = 'flex';
                                            }}
                                          />
                                          {/* Fallback when image fails to load */}
                                          <div className="image-fallback hidden absolute inset-0 items-center justify-center bg-gradient-to-br from-yellow-50 to-orange-50">
                                            <div className="text-center">
                                              <div className="text-4xl mb-2">🎨</div>
                                              <p className="text-sm text-orange-600 font-medium">
                                                Re-imagined Artwork
                                              </p>
                                              </div>
                                            </div>
                                          
                                          {/* Delete button on bottom right of image thumbnail */}
                                          <button
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              requestDeleteActivity(activity);
                                            }}
                                            className="absolute bottom-1 right-1 text-red-400 hover:text-red-600 p-1 rounded-full hover:bg-red-100 transition-all duration-200 opacity-70 hover:opacity-100 z-40"
                                            title="Delete this creation"
                                          >
                                            <FaTrash size={8} />
                                          </button>
                                              </div>
                                            </div>

                                      <div className="flex justify-between items-center mb-1 relative z-10">
                                        <div className="text-center flex-1">
                                          <p className="text-xs text-orange-700 font-medium">
                                            {formatDate(item.createdAt || activity.createdAt)}
                                          </p>
                                          </div>
                                        </div>

                                      {/* View Details Button */}
                                      <div className="relative z-10 mt-1">
                                        <button 
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleViewDetails(activity, artworkGroup);
                                          }}
                                          className="w-full bg-gradient-to-r from-orange-500 to-amber-400 hover:from-orange-600 hover:to-amber-500 text-white px-3 py-2 rounded-lg text-xs font-semibold transition-all duration-300 shadow-md hover:shadow-lg transform hover:scale-105"
                                          style={{ fontFamily: 'Baloo 2, Comic Sans MS, cursive' }}
                                        >
                                          View details
                                        </button>
                                          </div>
                                    </div>
                                  ));
                                })
                              ) : (
                                // Activity cards matching Magic Studio style
                                activities.map((activity, actIndex) => (
                                  <div key={`${feature}-${activity.id}-${actIndex}`}>
                                    {activity.type === 'appreciation' ? (
                                      /* Art Appreciation Card with thumbnail and 4 yellow buttons */
                                      <div
                                        className="bg-gradient-to-br from-orange-100 via-yellow-100 to-pink-100 rounded-xl p-3 cursor-pointer hover:shadow-lg transition-all duration-300 border-2 border-orange-300 hover:border-orange-400 relative overflow-hidden"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleViewDetails(activity, artworkGroup);
                                        }}
                                      >
                                        {/* Fun background pattern */}
                                        <div className="absolute inset-0 opacity-10">
                                          <div className="absolute top-2 right-2 text-4xl rotate-12">🎨</div>
                                          <div className="absolute bottom-2 left-2 text-3xl -rotate-12">✨</div>
                                          <div className="absolute top-1/2 left-1/2 text-2xl transform -translate-x-1/2 -translate-y-1/2 rotate-45">🌈</div>
                                        </div>

                                        {/* Strip Label */}
                                        <div className="absolute -top-1 left-4 bg-gradient-to-r from-orange-400 to-pink-400 text-white px-2 py-0.5 rounded-b text-xs font-bold shadow-md z-20">
                                          {getFeatureDisplayName(activity.feature)}
                                          </div>

                                        {/* Artwork Thumbnail */}
                                        <div className="relative mb-2 z-10 mt-3">
                                          <div className="relative h-28 w-full rounded-2xl overflow-hidden border-2 border-orange-300 bg-gradient-to-br from-yellow-50 to-orange-50 shadow-lg">
                                            <SmartImage
                                              src={artworkGroup.artworkUrl || activity.originalImageUrl || ''} 
                                              alt="Artwork for appreciation" 
                                              className="w-full h-full object-cover"
                                              containerClassName="w-full h-full"
                                              onError={(e) => {
                                                e.currentTarget.style.display = 'none';
                                                const fallback = e.currentTarget.parentElement.querySelector('.image-fallback');
                                                if (fallback) fallback.style.display = 'flex';
                                              }}
                                            />
                                            {/* Fallback when image fails to load */}
                                            <div className="image-fallback hidden absolute inset-0 items-center justify-center bg-gradient-to-br from-yellow-50 to-orange-50">
                                              <div className="text-center">
                                                <div className="text-4xl mb-2">🎨</div>
                                                <p className="text-sm text-orange-600 font-medium">
                                                  {getFeatureDisplayName(activity.feature)}
                                            </p>
                                          </div>
                                            </div>

                                            {/* 4 Yellow Appreciation Points */}
                                            <div className="absolute top-2 left-2 w-3 h-3 bg-yellow-400 rounded-full border border-yellow-500 shadow-sm z-30"></div>
                                            <div className="absolute top-2 right-2 w-3 h-3 bg-yellow-400 rounded-full border border-yellow-500 shadow-sm z-30"></div>
                                            <div className="absolute bottom-2 left-2 w-3 h-3 bg-yellow-400 rounded-full border border-yellow-500 shadow-sm z-30"></div>
                                            <div className="absolute bottom-2 right-2 w-3 h-3 bg-yellow-400 rounded-full border border-yellow-500 shadow-sm z-30"></div>
                                            
                                            {/* Delete button on bottom right of image thumbnail */}
                                                <button
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                requestDeleteActivity(activity);
                                              }}
                                              className="absolute bottom-1 right-1 text-red-400 hover:text-red-600 p-1 rounded-full hover:bg-red-100 transition-all duration-200 opacity-70 hover:opacity-100 z-40"
                                              title="Delete this creation"
                                            >
                                              <FaTrash size={8} />
                                                </button>
                                              </div>
                                            </div>

                                        <div className="flex justify-between items-center mb-1 relative z-10">
                                          <div className="text-center flex-1">
                                            <p className="text-xs text-orange-700 font-medium">
                                              {formatDate(activity.createdAt)}
                                            </p>
                                          </div>
                                        </div>

                                        {/* View Details Button */}
                                        <div className="relative z-10 mt-1">
                                          <button 
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleViewDetails(activity, artworkGroup);
                                            }}
                                            className="w-full bg-gradient-to-r from-orange-500 to-amber-400 hover:from-orange-600 hover:to-amber-500 text-white px-3 py-2 rounded-lg text-xs font-semibold transition-all duration-300 shadow-md hover:shadow-lg transform hover:scale-105"
                                            style={{ fontFamily: 'Baloo 2, Comic Sans MS, cursive' }}
                                          >
                                            View details
                                          </button>
                                  </div>
                                </div>
                              ) : (
                                      /* Regular activity card for non-appreciation activities */
                                      <div
                                        className="bg-gradient-to-br from-orange-100 via-yellow-100 to-pink-100 rounded-xl p-3 cursor-pointer hover:shadow-lg transition-all duration-300 border-2 border-orange-300 hover:border-orange-400 relative overflow-hidden"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleViewDetails(activity, artworkGroup);
                                        }}
                                      >
                                        {/* Fun background pattern */}
                                        <div className="absolute inset-0 opacity-10">
                                          <div className="absolute top-2 right-2 text-4xl rotate-12">🎨</div>
                                          <div className="absolute bottom-2 left-2 text-3xl -rotate-12">✨</div>
                                          <div className="absolute top-1/2 left-1/2 text-2xl transform -translate-x-1/2 -translate-y-1/2 rotate-45">🌈</div>
                                        </div>

                                        {/* Strip Label */}
                                        <div className="absolute -top-1 left-4 bg-gradient-to-r from-orange-400 to-pink-400 text-white px-2 py-0.5 rounded-b text-xs font-bold shadow-md z-20">
                                              {getFeatureDisplayName(activity.feature)}
                                          </div>

                                        {/* Activity Thumbnail */}
                                        <div className="relative mb-2 z-10 mt-3">
                                          <div className="relative h-28 w-full rounded-2xl overflow-hidden border-2 border-orange-300 bg-gradient-to-br from-yellow-50 to-orange-50 shadow-lg">
                                            <SmartImage
                                              src={artworkGroup.artworkUrl || activity.originalImageUrl || ''} 
                                              alt={`${activity.title || 'Activity'} artwork`} 
                                              className="w-full h-full object-cover"
                                              containerClassName="w-full h-full"
                                              onError={(e) => {
                                                e.currentTarget.style.display = 'none';
                                                const fallback = e.currentTarget.parentElement.querySelector('.image-fallback');
                                                if (fallback) fallback.style.display = 'flex';
                                              }}
                                            />
                                            {/* Fallback when image fails to load */}
                                            <div className="image-fallback hidden absolute inset-0 items-center justify-center bg-gradient-to-br from-yellow-50 to-orange-50">
                                              <div className="text-center">
                                                <div className="text-4xl mb-2">🎨</div>
                                                <p className="text-sm text-orange-600 font-medium">
                                                  {getFeatureDisplayName(activity.feature)}
                                                </p>
                                        </div>
                                            </div>
                                            
                                            {/* Delete button on bottom right of image thumbnail */}
                                            <button
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                requestDeleteActivity(activity);
                                              }}
                                              className="absolute bottom-1 right-1 text-red-400 hover:text-red-600 p-1 rounded-full hover:bg-red-100 transition-all duration-200 opacity-70 hover:opacity-100 z-40"
                                              title="Delete this creation"
                                            >
                                              <FaTrash size={8} />
                                            </button>
                                          </div>
                                        </div>

                                        <div className="flex justify-between items-center mb-1 relative z-10">
                                          <div className="text-center flex-1">
                                            <p className="text-xs text-orange-700 font-medium">
                                              {formatDate(activity.createdAt)}
                                            </p>
                                          </div>
                                        </div>

                                        {/* View Details Button */}
                                        <div className="relative z-10 mt-1">
                                            <button
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleViewDetails(activity, artworkGroup);
                                            }}
                                            className="w-full bg-gradient-to-r from-orange-500 to-amber-400 hover:from-orange-600 hover:to-amber-500 text-white px-3 py-2 rounded-lg text-xs font-semibold transition-all duration-300 shadow-md hover:shadow-lg transform hover:scale-105"
                                            style={{ fontFamily: 'Baloo 2, Comic Sans MS, cursive' }}
                                          >
                                            View details
                                            </button>
                                          </div>
                                        </div>
                                    )}
                                      </div>
                                ))
                              )}
                            </div>
                          );
                        });
                        
                        result.push(...otherActivities);
                        return result;
                      })()}
                        </div>
                      </div>
                      
                      {/* Delete button for entire group */}
                      <button
                        onClick={() => openDeleteArtworkGroup(artworkGroup)}
                        className="absolute top-4 right-4 p-2 text-gray-400 hover:text-red-600 transition-colors z-30"
                        title="Delete entire activity"
                      >
                        <FaTrash className="w-4 h-4" />
                      </button>
                    </div>
                  </motion.div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        <AnimatePresence>
        {showDetailsModal && selectedActivity && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[9999] p-4"
            onClick={(e) => {
              if (e.target === e.currentTarget) {
                setShowDetailsModal(false);
                setSelectedActivity(null);
                setSelectedArtworkGroup(null);
                setResolvedOriginalImageUrl(null);
              }
            }}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-2xl p-6 md:p-8 w-[95vw] max-w-6xl max-h-[90vh] overflow-hidden flex"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Content Area - Split View */}
              <div className="flex flex-1 flex-col min-h-0">
                {/* Meta inside content with padding (not on outer container) */}
                <div className="w-full mb-4">
                  <div className="bg-white rounded-xl p-4 border border-gray-200 flex items-start justify-between">
                    <div>
                      <h3 className="text-2xl font-bold text-gray-900">{selectedActivity.title || 'Activity Details'}</h3>
                      <p className="text-gray-600 text-sm">by {selectedActivity?.metadata?.artistName || 'Unknown Artist'}</p>
                      {(selectedActivity?.metadata?.className || selectedActivity?.metadata?.section) && (
                        <p className="text-amber-600 text-xs">
                          {selectedActivity?.metadata?.className} {selectedActivity?.metadata?.section ? `- ${selectedActivity.metadata.section}` : ''}
                        </p>
                      )}
                    </div>
                    <button
                      onClick={() => {
                        setShowDetailsModal(false);
                        setSelectedActivity(null);
                        setSelectedArtworkGroup(null);
                        setResolvedOriginalImageUrl(null);
                      }}
                      className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
                      aria-label="Close"
                    >
                      <FaTimes className="w-5 h-5" />
                    </button>
                  </div>
                </div>

                <div className="flex flex-1 min-h-0">
                {/* Left Side: Original Artwork (for reimagine/ways of seeing) or Artwork with hotspots (for appreciation) */}
                {(() => {
                  // Check if this is a reimagine or ways of seeing activity
                  const feature = selectedActivity?.feature || selectedActivity?.metadata?.feature || '';
                  const hasStyleInfo = !!selectedActivity?.styleInfo;
                  const hasTransformedImage = !!selectedActivity?.transformedImageUrl;
                  const isReimagine = feature === 'reimagine' || 
                                     feature === 'reimagine-with-feedback' || 
                                     feature === 'reimagine-artwork' ||
                                     feature === 'ways-of-seeing-journey';
                  
                  // Show original artwork on left if it's a reimagine/ways of seeing activity
                  const isReimagineOrWaysOfSeeing = isReimagine || (hasStyleInfo && hasTransformedImage);
                  
                  if (isReimagineOrWaysOfSeeing) {
                    // For reimagine/ways of seeing: Show original artwork on left
                    // Get original artwork URL - use the resolved URL from handleViewDetails, or try fallbacks
                    const getOriginalImageUrl = () => {
                      // First, try the resolved URL from useEffect (for gs:// URLs)
                      if (resolvedOriginalImageUrl && !isBlobUrl(resolvedOriginalImageUrl)) {
                        return resolvedOriginalImageUrl;
                      }
                      
                      // Then try the resolved URL that handleViewDetails set
                      if (selectedActivity.originalImageUrl && !isBlobUrl(selectedActivity.originalImageUrl)) {
                        return selectedActivity.originalImageUrl;
                      }
                      
                      // Try artworkUrl (which might be the resolved original)
                      if (selectedActivity.artworkUrl && !isBlobUrl(selectedActivity.artworkUrl)) {
                        return selectedActivity.artworkUrl;
                      }
                      
                      // Try metadata sources
                      if (selectedActivity.metadata?.originalImageUrl && !isBlobUrl(selectedActivity.metadata.originalImageUrl)) {
                        return selectedActivity.metadata.originalImageUrl;
                      }
                      if (selectedActivity.metadata?.originalArtworkUrl && !isBlobUrl(selectedActivity.metadata.originalArtworkUrl)) {
                        return selectedActivity.metadata.originalArtworkUrl;
                      }
                      
                      // Try nested activity sources
                      if (selectedActivity.activities?.[0]?.originalImageUrl && !isBlobUrl(selectedActivity.activities[0].originalImageUrl)) {
                        return selectedActivity.activities[0].originalImageUrl;
                      }
                      
                      // Fallback to artworkGroup's artworkUrl if available
                      if (selectedArtworkGroup?.artworkUrl && !isBlobUrl(selectedArtworkGroup.artworkUrl)) {
                        return selectedArtworkGroup.artworkUrl;
                      }
                      
                      // Last resort: use resolved URL or blob URL if that's all we have
                      return resolvedOriginalImageUrl || selectedActivity.originalImageUrl || selectedActivity.artworkUrl || selectedArtworkGroup?.artworkUrl || null;
                    };
                    
                    const originalImageUrl = getOriginalImageUrl();
                    
                    console.log('🔍 Modal - Original artwork URL resolution:', {
                      selectedActivityOriginalImageUrl: selectedActivity.originalImageUrl,
                      selectedActivityArtworkUrl: selectedActivity.artworkUrl,
                      metadataOriginalImageUrl: selectedActivity.metadata?.originalImageUrl,
                      metadataOriginalArtworkUrl: selectedActivity.metadata?.originalArtworkUrl,
                      finalResolvedUrl: originalImageUrl,
                      isBlob: isBlobUrl(originalImageUrl),
                      isDataUrl: originalImageUrl?.startsWith('data:image/'),
                      isGsUrl: originalImageUrl?.startsWith('gs://')
                    });
                    
                    return (
                      <div className="w-1/2 p-4 relative flex items-center justify-center h-[65vh]">
                        <div className="w-full h-full flex flex-col">
                          <h3 className="text-lg font-bold text-purple-800 mb-2">Original Artwork</h3>
                          <div className="relative flex-1 rounded-lg overflow-hidden border-4 border-purple-200">
                            {originalImageUrl ? (
                              <img
                                src={(() => {
                                  // Proxy Firebase Storage URLs if they might have CORS issues
                                  const url = originalImageUrl;
                                  if (url.includes('firebasestorage.googleapis.com') || url.includes('storage.googleapis.com')) {
                                    // Try direct first, but have proxy as fallback
                                    return url;
                                  }
                                  return url;
                                })()}
                                alt="Original artwork"
                                className="w-full h-full object-contain"
                                onError={(e) => {
                                  console.error('❌ Failed to load original artwork:', originalImageUrl);
                                  // Try proxy if it's a Firebase Storage URL
                                  if (originalImageUrl && (originalImageUrl.includes('firebasestorage.googleapis.com') || originalImageUrl.includes('storage.googleapis.com'))) {
                                    // Try proxy for Firebase Storage URLs that might have CORS issues
                                    const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001';
                                    const proxyUrl = `${backendUrl}/proxy-image?url=${encodeURIComponent(originalImageUrl)}`;
                                    console.log('🔄 Retrying with proxy URL:', proxyUrl);
                                    e.target.src = proxyUrl;
                                    return;
                                  }
                                  // Also try to get from artworkGroup if available
                                  if (selectedArtworkGroup?.artworkUrl && selectedArtworkGroup.artworkUrl !== originalImageUrl) {
                                    console.log('🔄 Trying artworkGroup URL as fallback:', selectedArtworkGroup.artworkUrl);
                                    e.target.src = selectedArtworkGroup.artworkUrl;
                                    return;
                                  }
                                  e.target.style.display = 'none';
                                  const fallback = e.target.parentElement.querySelector('.artwork-fallback');
                                  if (fallback) fallback.style.display = 'flex';
                                }}
                                onLoad={() => {
                                  console.log('✅ Original artwork loaded successfully:', originalImageUrl);
                                }}
                              />
                            ) : (
                              <div className="artwork-fallback flex absolute inset-0 items-center justify-center bg-gradient-to-br from-purple-50 to-indigo-50">
                                <div className="text-center">
                                  <div className="text-4xl mb-2">🎨</div>
                                  <p className="text-sm text-purple-600 font-medium">Original Artwork</p>
                                  <p className="text-xs text-purple-500 mt-1">Image not available</p>
                                </div>
                              </div>
                            )}
                            <div className="artwork-fallback absolute inset-0 items-center justify-center bg-gradient-to-br from-purple-50 to-indigo-50" style={{ display: originalImageUrl ? 'none' : 'flex' }}>
                              <div className="text-center">
                                <div className="text-4xl mb-2">🎨</div>
                                <p className="text-sm text-purple-600 font-medium">Original Artwork</p>
                                <p className="text-xs text-purple-500 mt-1">Image not available</p>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  } else {
                    // For appreciation: Show artwork with hotspots
                    return (
                      <div className="w-1/2 p-4 relative flex items-center justify-center h-[65vh]">
                        <img
                          src={selectedActivity.originalImageUrl}
                          alt="Original artwork"
                          className="max-w-full max-h-full object-contain rounded-lg shadow-md"
                        />
                        {Array.isArray(selectedActivity?.aiPoints) && selectedActivity.aiPoints.length > 0 && (
                          <div className="absolute inset-0">
                            {selectedActivity.aiPoints.map((p, i) => (
                              <button
                                key={i}
                                onClick={() => { setSelectedPointIdx(i); setDetailsOpen(prev => ({ ...prev, appreciation: true })); }}
                                className={`absolute w-10 h-10 text-white rounded-full flex items-center justify-center text-sm font-bold shadow-lg transition-all duration-200 ${
                                  selectedPointIdx === i ? 'bg-amber-600 scale-110 ring-4 ring-amber-200' : 'bg-amber-500 hover:bg-amber-600 hover:scale-105'
                                }`}
                                style={{ left: `15%`, top: `${(0.2 + (i * 0.2)) * 100}%`, transform: 'translate(-50%, -50%)' }}
                                title={`Point ${i + 1}`}
                              >
                                {i + 1}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  }
                })()}

                {/* Right Side: Details Panel */}
                <div className="w-1/2 p-4 overflow-y-auto overscroll-contain space-y-6 h-[65vh]">
                  {/* Did YOU KNOW? */}
                  {Array.isArray(selectedActivity?.aiPoints) && selectedActivity.aiPoints.length > 0 && (
                    <div className="bg-white rounded-xl p-6 border border-amber-200">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="font-semibold text-gray-900 flex items-center">
                          {selectedPointIdx !== null && (
                            <span className="w-8 h-8 rounded-full border border-amber-300 text-amber-700 flex items-center justify-center text-sm font-bold mr-3">
                              {selectedPointIdx + 1}
                            </span>
                          )}
                          <span className="tracking-tight text-xl">Did YOU KNOW?</span>
                        </h3>
                        {selectedPointIdx !== null && (
                          <button
                            onClick={() => setSelectedPointIdx(null)}
                            className="ml-3 px-3 py-2 text-sm bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg border border-gray-300"
                          >
                            Close
                          </button>
                        )}
                      </div>
                      {selectedPointIdx !== null ? (
                        (() => {
                          const p = selectedActivity.aiPoints[selectedPointIdx] || {};
                          return (
                            <>
                              <p className="text-gray-700 leading-relaxed text-lg mb-4">{p.text}</p>
                              {Array.isArray(p.improve) && p.improve.length > 0 && (
                                <div className="bg-gray-50 rounded-lg p-5 border border-gray-200">
                                  <p className="text-sm font-medium text-gray-800 mb-3">Artmosphere tips</p>
                                  <ul className="space-y-2 list-disc list-inside">
                                    {p.improve.map((tip, ti) => (
                                      <li key={ti} className="text-gray-800 text-base leading-relaxed">{tip}</li>
                                    ))}
                                  </ul>
                                </div>
                              )}
                            </>
                          );
                        })()
                      ) : (
                        <p className="text-gray-600">Click a numbered point on the artwork to see specific appreciation and tips.</p>
                      )}
                    </div>
                  )}

                  {/* Reimagine/Ways of Seeing Activity Details - Show transformed artwork on right side */}
                  {(() => {
                    // Check if this is a reimagine or ways of seeing activity (has transformedImageUrl and styleInfo or reimagine feature)
                    const hasTransformedImage = !!selectedActivity?.transformedImageUrl;
                    const hasStyleInfo = !!selectedActivity?.styleInfo;
                    const feature = selectedActivity?.feature || selectedActivity?.metadata?.feature || '';
                    const isReimagine = feature === 'reimagine' || 
                                       feature === 'reimagine-with-feedback' || 
                                       feature === 'reimagine-artwork' ||
                                       feature === 'ways-of-seeing-journey';
                    
                    // Show transformed artwork if it's a reimagine activity OR if it has styleInfo (ways of seeing)
                    const shouldShowTransformed = hasTransformedImage && (isReimagine || hasStyleInfo);
                    
                    console.log('🔍 Debug selectedActivity:', {
                      feature: feature,
                      metadata: selectedActivity?.metadata,
                      transformedImageUrl: selectedActivity?.transformedImageUrl,
                      hasStyleInfo: hasStyleInfo,
                      hasTransformedImage: hasTransformedImage,
                      shouldShowTransformed: shouldShowTransformed
                    });
                    
                    return shouldShowTransformed;
                  })() && (
                    <div className="space-y-6">
                      {/* Transformed Artwork (large) */}
                      <div className="bg-white rounded-xl p-4 border border-gray-200">
                        <h3 className="font-semibold text-gray-900 text-xl mb-3">
                          {selectedActivity?.styleInfo?.name ? 
                            `${selectedActivity.styleInfo.name} Style Transformation` : 
                            'Artwork Transformation'}
                        </h3>
                        <div className="w-full h-[40vh] bg-gray-100 rounded-lg overflow-hidden flex items-center justify-center">
                          {(() => {
                            // Get transformed image URL - check multiple sources including journey steps
                            const transformedUrl = selectedActivity.transformedImageUrl || 
                                                  selectedActivity.imageUrl ||
                                                  (selectedActivity.journey && selectedActivity.journey.length > 0 ? 
                                                    selectedActivity.journey[selectedActivity.journey.length - 1].transformedImageUrl : null);
                            
                            return transformedUrl ? (
                              <SmartImage
                                src={transformedUrl} 
                                alt={selectedActivity?.styleInfo?.name ? 
                                  `${selectedActivity.styleInfo.name} transformed artwork` : 
                                  'Transformed artwork'}
                                className="max-w-full max-h-full object-contain"
                                containerClassName="max-w-full max-h-full"
                            onError={(e) => {
                                  console.error('❌ Failed to load transformed artwork:', transformedUrl);
                                  e.currentTarget.style.display = 'none';
                              const fallback = e.currentTarget.nextElementSibling;
                              if (fallback) fallback.style.display = 'flex';
                            }}
                                onLoad={() => {
                                  console.log('✅ Transformed artwork loaded successfully:', transformedUrl);
                                }}
                              />
                            ) : (
                              <div className="w-full h-full flex bg-gray-200 items-center justify-center text-gray-500">
                                No transformed image available
                              </div>
                            );
                          })()}
                          <div className="w-full h-full hidden bg-gray-200 items-center justify-center text-gray-500">
                            No Image
                          </div>
                        </div>
                      </div>

                      {/* Style Information for Ways of Seeing */}
                      {selectedActivity?.styleInfo?.name && (
                        <div className="bg-gradient-to-r from-amber-100 to-orange-100 rounded-xl p-4 border-2 border-amber-300">
                          <h3 className="font-semibold text-amber-800 text-base mb-2">Art Style: {selectedActivity.styleInfo.name}</h3>
                          {selectedActivity.styleInfo.description && (
                            <p className="text-amber-700 leading-relaxed text-sm">{selectedActivity.styleInfo.description}</p>
                          )}
                        </div>
                      )}

                      {/* Prompt Section */}
                      {selectedActivity?.metadata?.prompt && (
                        <div className="bg-gradient-to-r from-pink-100 to-orange-100 rounded-xl p-4 border-2 border-pink-300">
                          <h3 className="font-semibold text-pink-800 text-base mb-2">How You Reimagined Your Artwork</h3>
                          <p className="text-pink-700 leading-relaxed text-sm">{selectedActivity.metadata.prompt}</p>
                        </div>
                      )}

                      {/* User Reflection */}
                      {selectedActivity?.metadata?.userFeedback && (
                        <div className="bg-gradient-to-r from-yellow-50 to-amber-50 rounded-xl p-4 border-2 border-yellow-300">
                          <h3 className="font-semibold text-yellow-800 text-base mb-2">Your Reflection</h3>
                          <p className="text-yellow-700 leading-relaxed text-sm">{selectedActivity.metadata.userFeedback}</p>
                        </div>
                      )}

                      {/* Artmosphere's Insights */}
                      {selectedActivity?.metadata?.artmosphereFeedback && (
                        <div className="space-y-3">
                          <div className="flex items-center gap-2 mb-2">
                            <FaComments className="text-green-600" />
                            <h3 className="font-semibold text-green-800 text-base">Artmosphere's Insights</h3>
                          </div>
                          <div className="space-y-3">
                            {Array.isArray(selectedActivity.metadata.artmosphereFeedback) ? (
                              selectedActivity.metadata.artmosphereFeedback
                                .filter((fb) => {
                                  const isIntro = (fb.title || '').toLowerCase() === 'intro';
                                  const introText = fb.text || fb.appreciation || '';
                                  // Hide trivial intros
                                  if (isIntro && introText.length < 80) return false;
                                  return true;
                                })
                                .map((feedback, idx) => {
                                  const isIntro = (feedback.title || '').toLowerCase() === 'intro';
                                  const hasSuggestionOnly = !!feedback.suggestion && !(feedback.text || feedback.appreciation);
                                  const displayTitle = hasSuggestionOnly
                                    ? "Mentor's Suggestion"
                                    : (isIntro ? 'Intro' : (feedback.title || 'Impact of Your Change'));
                                  const displayText = feedback.text || feedback.appreciation || '';
                                  return (
                                    <div key={idx} className="bg-gradient-to-r from-yellow-50 to-amber-50 rounded-xl p-4 border-2 border-yellow-300">
                                      <div className="flex items-center justify-between mb-2">
                                        <h4 className="font-semibold text-yellow-800 text-sm">{displayTitle}</h4>
                                        <span className="text-yellow-600 text-xs">▼</span>
                                      </div>
                                      {displayText && (
                                        <p className="text-yellow-700 leading-relaxed text-sm">{displayText}</p>
                                      )}
                                  {feedback.suggestion && (
                                        <div className="mt-3 pt-3 border-t border-yellow-300">
                                          <p className="text-yellow-700 text-sm leading-relaxed">{feedback.suggestion}</p>
                                    </div>
                                  )}
                                </div>
                                  );
                                })
                            ) : (
                              <div className="bg-gradient-to-r from-yellow-50 to-amber-50 rounded-xl p-4 border-2 border-yellow-300">
                                <p className="text-yellow-700 leading-relaxed text-sm">{selectedActivity.metadata.artmosphereFeedback}</p>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Techniques Used */}
                  {(() => {
                    const techniquesSrc = Array.isArray(selectedActivity?.techniques) && selectedActivity.techniques.length > 0
                      ? selectedActivity.techniques
                      : (selectedActivity.artAnalysis?.techniques || []);
                    const mainTechnique = techniquesSrc && techniquesSrc.length > 0 ? techniquesSrc[0] : null;
                    return mainTechnique ? (
                      <div className="bg-white rounded-2xl border border-amber-200 overflow-hidden">
                        <button
                          onClick={() => setDetailsOpen(prev => ({ ...prev, techniques: !prev.techniques }))}
                          className="w-full p-6 text-left hover:bg-amber-50 transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-inset"
                        >
                          <div className="flex items-center justify-between">
                           <h3 className="text-lg font-bold text-gray-900">Did you know this technique looks like?</h3>
                            <div className={`transform transition-transform duration-300 ${detailsOpen.techniques ? 'rotate-180' : 'rotate-0'}`}>▼</div>
                          </div>
                        </button>
                        {detailsOpen.techniques && (
                          <div className="px-6 pb-6 space-y-4">
                            <div className="bg-gray-50 rounded-lg p-4">
                              <h5 className="font-semibold text-gray-900 text-lg mb-2">{mainTechnique.name}</h5>
                              {mainTechnique.description && (<p className="text-gray-700 mb-2">{mainTechnique.description}</p>)}
                            </div>
                          </div>
                        )}
                      </div>
                    ) : null;
                  })()}

                  {/* Art Movements */}
                  {selectedActivity?.artAnalysis?.movements?.length > 0 && (
                    <div className="bg-white rounded-2xl border border-blue-200 overflow-hidden">
                      <button
                        onClick={() => setDetailsOpen(prev => ({ ...prev, movements: !prev.movements }))}
                        className="w-full p-6 text-left hover:bg-blue-50 transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-inset"
                      >
                        <div className="flex items-center justify-between">
                          <h3 className="text-lg font-bold text-gray-900">Which Art movement this artwork could belong to?</h3>
                          <div className={`transform transition-transform duration-300 ${detailsOpen.movements ? 'rotate-180' : 'rotate-0'}`}>▼</div>
                        </div>
                      </button>
                      {detailsOpen.movements && (
                        <div className="px-6 pb-6 space-y-4">
                          {selectedActivity.artAnalysis.movements.map((m, idx) => (
                            <div key={idx} className="bg-gray-50 rounded-lg p-4">
                              <h5 className="font-semibold text-gray-900 text-lg mb-2">{m.name}</h5>
                              <p className="text-gray-700 mb-2">{m.description}</p>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Key Concepts */}
                  {Array.isArray(selectedActivity?.concepts) && selectedActivity.concepts.length > 0 && (
                    <div className="bg-white rounded-2xl border border-green-200 overflow-hidden">
                      <button
                        onClick={() => setDetailsOpen(prev => ({ ...prev, concepts: !prev.concepts }))}
                        className="w-full p-6 text-left hover:bg-green-50 transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-inset"
                      >
                        <div className="flex items-center justify-between">
                          <h3 className="text-lg font-bold text-gray-900">Key Concepts</h3>
                          <div className={`transform transition-transform duration-300 ${detailsOpen.concepts ? 'rotate-180' : 'rotate-0'}`}>▼</div>
                        </div>
                      </button>
                      {detailsOpen.concepts && (
                        <div className="px-6 pb-6 space-y-4">
                          {selectedActivity.concepts.map((c, idx) => (
                            <div key={idx} className="bg-gray-50 rounded-lg p-4">
                              <h5 className="font-semibold text-gray-900 text-lg mb-2">{c.title}</h5>
                              <p className="text-gray-700">{c.text}</p>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
        </AnimatePresence>

        {/* Delete sub-card modal */}
        <AnimatePresence>
        {showDeleteSubModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            onClick={() => setShowDeleteSubModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-2xl w-full max-w-md shadow-2xl border border-gray-200"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-full bg-red-100 text-red-600 flex items-center justify-center">!</div>
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900">Delete this activity?</h3>
                    <p className="text-sm text-gray-600 mt-1">This will remove <span className="font-medium">{deleteSubTarget?.title || 'the selected activity'}</span> from the dashboard. This action cannot be undone.</p>
                  </div>
                </div>
                <div className="mt-6 flex justify-end gap-3">
                  <button
                    className="px-4 py-2 rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
                    onClick={() => setShowDeleteSubModal(false)}
                  >
                    Cancel
                  </button>
                  <button
                    className="px-4 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700"
                    onClick={handleDeleteActivity}
                  >
                    Delete
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
        </AnimatePresence>

        {/* Delete Confirmation Modal */}
        <AnimatePresence>
        {showDeleteModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            onClick={() => setShowDeleteModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-2xl w-full max-w-md shadow-2xl border border-gray-200"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-full bg-red-100 text-red-600 flex items-center justify-center">!</div>
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900">Delete artwork activity?</h3>
                    <p className="text-sm text-gray-600 mt-1">This will remove the entire activity and all sub-cards for <span className="font-medium">{deleteTarget?.title || 'this artwork'}</span>. This action cannot be undone.</p>
                  </div>
                </div>
                <div className="mt-6 flex justify-end gap-3">
                  <button
                    className="px-4 py-2 rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
                    onClick={() => setShowDeleteModal(false)}
                  >
                    Cancel
                  </button>
                  <button
                    className="px-4 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700"
                    onClick={handleDeleteArtworkGroup}
                  >
                    Delete
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
        </AnimatePresence>

        {/* Share Artwork Modal - Artwork Selection */}
        <AnimatePresence>
        {showShareModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[10000] p-4"
            onClick={() => setShowShareModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-2xl w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="bg-gradient-to-r from-orange-500 to-amber-400 px-6 py-4 flex items-center justify-between">
                <div>
                  <h2 className="text-2xl font-bold text-white">Share Artwork</h2>
                  <p className="text-orange-100 text-sm mt-1">Showcase beautiful artworks of your students with other Mentors</p>
                </div>
                <button
                  onClick={() => setShowShareModal(false)}
                  className="text-white hover:text-orange-100 transition-colors"
                >
                  <FaTimes className="w-6 h-6" />
                </button>
              </div>

              {/* Artwork Scroller */}
              <div className="flex-1 overflow-hidden flex flex-col md:flex-row">
                {/* Left: Artwork Display with Scroller */}
                <div className="w-full md:w-2/3 p-6 bg-gray-50 flex flex-col min-h-[400px]">
                  {allArtworks.length > 0 ? (
                    <>
                      {/* Selected Artwork Large View */}
                      {selectedArtworkForShare && (
                        <div className="mb-4 flex-shrink-0">
                          <div className="relative w-full max-w-2xl mx-auto">
                            <img
                              src={selectedArtworkForShare.artworkUrl}
                              alt={selectedArtworkForShare.title}
                              className="w-full h-auto rounded-lg shadow-lg border-4 border-white max-h-[300px] object-contain"
                              onError={(e) => {
                                e.target.style.display = 'none';
                                const fallback = e.target.parentElement.querySelector('.artwork-fallback');
                                if (fallback) fallback.style.display = 'flex';
                              }}
                            />
                            <div className="artwork-fallback hidden absolute inset-0 items-center justify-center bg-gray-200 rounded-lg">
                              <div className="text-center">
                                <div className="text-4xl mb-2">🎨</div>
                                <p className="text-gray-600">Artwork not available</p>
                              </div>
                            </div>
                          </div>
                          <div className="text-center mt-2">
                            <h3 className="text-lg font-bold text-gray-900">{selectedArtworkForShare.title}</h3>
                            <p className="text-sm text-gray-600">by {selectedArtworkForShare.artistName}</p>
                            {selectedArtworkForShare.className && (
                              <p className="text-xs text-gray-500 mt-1">
                                {selectedArtworkForShare.className} {selectedArtworkForShare.section ? `- ${selectedArtworkForShare.section}` : ''}
                              </p>
                            )}
                          </div>
                        </div>
                      )}
                      
                      {/* Artwork Thumbnail Scroller */}
                      <div className="flex-1 overflow-y-auto">
                        <h4 className="text-sm font-semibold text-gray-700 mb-3">Select an artwork to share:</h4>
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                          {allArtworks.map((artwork, index) => (
                            <div
                              key={artwork.id || index}
                              onClick={() => {
                                setCurrentArtworkIndex(index);
                                setSelectedArtworkForShare(artwork);
                                setShareFormData({
                                  studentName: artwork.artistName !== 'Unknown Artist' ? artwork.artistName : shareFormData.studentName,
                                  className: artwork.className || shareFormData.className,
                                  section: artwork.section || shareFormData.section,
                                  schoolName: shareFormData.schoolName,
                                  mentorComments: shareFormData.mentorComments
                                });
                              }}
                              className={`relative cursor-pointer rounded-lg overflow-hidden border-2 transition-all ${
                                selectedArtworkForShare?.id === artwork.id || selectedArtworkForShare?.artworkUrl === artwork.artworkUrl
                                  ? 'border-orange-500 shadow-lg scale-105'
                                  : 'border-gray-200 hover:border-orange-300 hover:shadow-md'
                              }`}
                            >
                              <div className="aspect-square relative bg-gray-100">
                                <img
                                  src={artwork.artworkUrl}
                                  alt={artwork.title}
                                  className="w-full h-full object-cover"
                                  onError={(e) => {
                                    e.target.style.display = 'none';
                                    const fallback = e.target.parentElement.querySelector('.thumb-fallback');
                                    if (fallback) fallback.style.display = 'flex';
                                  }}
                                />
                                <div className="thumb-fallback hidden absolute inset-0 items-center justify-center bg-gray-200">
                                  <div className="text-center">
                                    <div className="text-2xl mb-1">🎨</div>
                                  </div>
                                </div>
                                {selectedArtworkForShare?.id === artwork.id || selectedArtworkForShare?.artworkUrl === artwork.artworkUrl ? (
                                  <div className="absolute top-1 right-1 bg-orange-500 text-white rounded-full p-1">
                                    <FaCheckCircle className="w-4 h-4" />
                                  </div>
                                ) : null}
                              </div>
                              <div className="p-2 bg-white">
                                <p className="text-xs font-medium text-gray-900 truncate" title={artwork.title}>
                                  {artwork.title}
                                </p>
                                <p className="text-xs text-gray-500 truncate">
                                  {artwork.artistName !== 'Unknown Artist' ? artwork.artistName : 'Unknown'}
                                </p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </>
                  ) : (
                    <div className="flex-1 flex items-center justify-center text-center text-gray-500">
                      <div>
                        <div className="text-6xl mb-4">🎨</div>
                        <p className="text-lg">No artworks available to share</p>
                        <p className="text-sm text-gray-400 mt-2">Upload artworks to share them with other mentors</p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Right: Share Form */}
                <div className="w-full md:w-1/3 p-6 overflow-y-auto bg-white border-t md:border-t-0 md:border-l border-gray-200">
                  <h3 className="text-lg font-bold text-gray-900 mb-4">Artwork Details</h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Student Name *</label>
                      <input
                        type="text"
                        value={shareFormData.studentName}
                        onChange={(e) => setShareFormData({ ...shareFormData, studentName: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                        placeholder="Enter student name"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Class *</label>
                      <input
                        type="text"
                        value={shareFormData.className}
                        onChange={(e) => setShareFormData({ ...shareFormData, className: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                        placeholder="e.g., Class 7"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Section *</label>
                      <input
                        type="text"
                        value={shareFormData.section}
                        onChange={(e) => setShareFormData({ ...shareFormData, section: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                        placeholder="e.g., A, B, C"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">School Name *</label>
                      <input
                        type="text"
                        value={shareFormData.schoolName}
                        onChange={(e) => setShareFormData({ ...shareFormData, schoolName: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                        placeholder="Enter school name"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Mentor Comments *</label>
                      <textarea
                        value={shareFormData.mentorComments}
                        onChange={(e) => setShareFormData({ ...shareFormData, mentorComments: e.target.value })}
                        rows={4}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                        placeholder="Share tips, techniques, and what students learned from this artwork..."
                      />
                    </div>
                    <button
                      onClick={() => {
                        if (!shareFormData.studentName || !shareFormData.className || !shareFormData.section || !shareFormData.schoolName || !shareFormData.mentorComments) {
                          alert('Please fill in all required fields');
                          return;
                        }
                        setShowShareModal(false);
                        setShowShareOptionsModal(true);
                      }}
                      className="w-full px-4 py-3 bg-gradient-to-r from-orange-500 to-amber-400 hover:from-orange-600 hover:to-amber-500 text-white rounded-lg font-semibold transition-all duration-300 shadow-md hover:shadow-lg"
                      style={{ fontFamily: 'Baloo 2, Comic Sans MS, cursive' }}
                    >
                      Continue to Share Options
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
        </AnimatePresence>

        {/* Share Options Modal */}
        <AnimatePresence>
        {showShareOptionsModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[10001] p-4"
            onClick={() => setShowShareOptionsModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-2xl w-full max-w-2xl shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="bg-gradient-to-r from-orange-500 to-amber-400 px-6 py-4 flex items-center justify-between">
                <h2 className="text-2xl font-bold text-white">Share Options</h2>
                <button
                  onClick={() => setShowShareOptionsModal(false)}
                  className="text-white hover:text-orange-100 transition-colors"
                >
                  <FaTimes className="w-6 h-6" />
                </button>
              </div>

              {/* Content */}
              <div className="p-6 space-y-6">
                <div className="space-y-4">
                  <label className="flex items-start gap-3 cursor-pointer group">
                    <input
                      type="checkbox"
                      checked={shareOptions.artmosphereMentors}
                      onChange={(e) => setShareOptions({ ...shareOptions, artmosphereMentors: e.target.checked })}
                      className="mt-1 w-5 h-5 text-orange-600 border-gray-300 rounded focus:ring-orange-500"
                    />
                    <div className="flex-1">
                      <div className="font-semibold text-gray-900">Artmosphere Mentors</div>
                      <div className="text-sm text-gray-600">Share with all Artmosphere mentors in the learning space</div>
                    </div>
                  </label>

                  <label className="flex items-start gap-3 cursor-pointer group">
                    <input
                      type="checkbox"
                      checked={shareOptions.organizationMentors}
                      onChange={(e) => setShareOptions({ ...shareOptions, organizationMentors: e.target.checked })}
                      className="mt-1 w-5 h-5 text-orange-600 border-gray-300 rounded focus:ring-orange-500"
                    />
                    <div className="flex-1">
                      <div className="font-semibold text-gray-900">Your Organization Mentors</div>
                      <div className="text-sm text-gray-600">Share with mentors in your organization</div>
                    </div>
                  </label>

                  {shareOptions.organizationMentors && (
                    <div className="ml-8 mt-2">
                      <label className="flex items-start gap-3 cursor-pointer group">
                        <input
                          type="checkbox"
                          checked={shareOptions.allowAccessCode}
                          onChange={(e) => setShareOptions({ ...shareOptions, allowAccessCode: e.target.checked })}
                          className="mt-1 w-5 h-5 text-orange-600 border-gray-300 rounded focus:ring-orange-500"
                        />
                        <div className="flex-1">
                          <div className="font-medium text-gray-900">Allow Access Code</div>
                          <div className="text-sm text-gray-600">Enable access for non-logged-in users with an access code</div>
                        </div>
                      </label>
                    </div>
                  )}
                </div>

                <div className="flex gap-3 pt-4 border-t border-gray-200">
                  <button
                    onClick={() => {
                      setShowShareOptionsModal(false);
                      setShowShareModal(true);
                    }}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    Back
                  </button>
                  <button
                    onClick={handleShareArtwork}
                    disabled={!shareOptions.artmosphereMentors && !shareOptions.organizationMentors}
                    className="flex-1 px-4 py-2 bg-gradient-to-r from-orange-500 to-amber-400 hover:from-orange-600 hover:to-amber-500 disabled:from-gray-300 disabled:to-gray-400 disabled:cursor-not-allowed text-white rounded-lg font-semibold transition-all duration-300 shadow-md hover:shadow-lg"
                    style={{ fontFamily: 'Baloo 2, Comic Sans MS, cursive' }}
                  >
                    Share Artwork
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
        </AnimatePresence>
      </div>
    </MentorAccessCheck>
  );
}
